import * as React from 'react';
import React__default, { useContext, useState } from 'react';
import styled, { ThemeContext, createGlobalStyle } from 'styled-components';

var lib = {};

var symbols = {};

/**
 * Symbols used internally within ts-pattern to construct and discriminate
 * Guard, Not, and Select, and AnonymousSelect patterns
 *
 * Symbols have the advantage of not appearing in auto-complete suggestions in
 * user defined patterns, and eliminate the admittedly unlikely risk of property
 * overlap between ts-pattern internals and user defined patterns.
 *
 * These symbols have to be visible to tsc for type inference to work, but
 * users should not import them
 * @module
 * @private
 * @internal
 */
Object.defineProperty(symbols, "__esModule", { value: true });
symbols.AnonymousSelectKey = symbols.Select = symbols.Not = symbols.Guard = symbols.PatternKind = void 0;
/** @internal This symbol should only be used by ts-pattern's internals. */
symbols.PatternKind = Symbol('@ts-pattern/pattern-kind');
/** @internal This symbol should only be used by ts-pattern's internals. */
symbols.Guard = Symbol('@ts-pattern/guard');
/** @internal This symbol should only be used by ts-pattern's internals. */
symbols.Not = Symbol('@ts-pattern/not');
/** @internal This symbol should only be used by ts-pattern's internals. */
symbols.Select = Symbol('@ts-pattern/select');
symbols.AnonymousSelectKey = '@ts-pattern/__anonymous-select-key';

var guards = {};

(function (exports) {
Object.defineProperty(exports, "__esModule", { value: true });
exports.instanceOf = exports.select = exports.not = exports.when = void 0;
const symbols$1 = symbols;
const when = (predicate) => ({
    [symbols$1.PatternKind]: symbols$1.Guard,
    [symbols$1.Guard]: predicate,
});
exports.when = when;
const not = (pattern) => ({
    [symbols$1.PatternKind]: symbols$1.Not,
    [symbols$1.Not]: pattern,
});
exports.not = not;
function select(key) {
    return key === undefined
        ? {
            [symbols$1.PatternKind]: symbols$1.Select,
            [symbols$1.Select]: symbols$1.AnonymousSelectKey,
        }
        : {
            [symbols$1.PatternKind]: symbols$1.Select,
            [symbols$1.Select]: key,
        };
}
exports.select = select;
function isInstanceOf(classConstructor) {
    return (val) => val instanceof classConstructor;
}
const instanceOf = (classConstructor) => (0, exports.when)(isInstanceOf(classConstructor));
exports.instanceOf = instanceOf;
}(guards));

var wildcards = {};

Object.defineProperty(wildcards, "__esModule", { value: true });
wildcards.__ = void 0;
const guards_1 = guards;
function isUnknown(x) {
    return true;
}
function isNumber(x) {
    return typeof x === 'number';
}
function numberIsNaN(x) {
    return Number.isNaN(x);
}
function isString(x) {
    return typeof x === 'string';
}
function isBoolean(x) {
    return typeof x === 'boolean';
}
function isNullish(x) {
    return x === null || x === undefined;
}
const unknownGuard = (0, guards_1.when)(isUnknown);
const stringGuard = (0, guards_1.when)(isString);
const numberGuard = (0, guards_1.when)(isNumber);
const NaNGuard = (0, guards_1.when)(numberIsNaN);
const booleanGuard = (0, guards_1.when)(isBoolean);
const nullishGuard = (0, guards_1.when)(isNullish);
/**
 * ### Catch All wildcard
 * `__` is wildcard pattern, matching **any value**.
 *
 * `__.string` is wildcard pattern matching any **string**.
 *
 * `__.number` is wildcard pattern matching any **number**.
 *
 * `__.NaN` is wildcard pattern matching **NaN**
 *
 * `__.boolean` is wildcard pattern matching any **boolean**.
 *
 * `__.nullish` is wildcard pattern matching **null** or **undefined**.
 * @example
 *  match(value)
 *   .with(__, () => 'will always match')
 *   .with(__.string, () => 'will match on strings only')
 *   .with(__.number, () => 'will match on numbers only')
 *   .with(__.NaN, () => 'will match on NaN')
 *   .with(__.boolean, () => 'will match on booleans only')
 *   .with(__.nullish, () => 'will match on null or undefined only')
 */
wildcards.__ = Object.assign(unknownGuard, {
    string: stringGuard,
    number: numberGuard,
    NaN: NaNGuard,
    boolean: booleanGuard,
    nullish: nullishGuard,
});

(function (exports) {
Object.defineProperty(exports, "__esModule", { value: true });
exports.isMatching = exports.match = exports.instanceOf = exports.select = exports.not = exports.when = exports.__ = void 0;
const symbols$1 = symbols;
const guards_1 = guards;
Object.defineProperty(exports, "when", { enumerable: true, get: function () { return guards_1.when; } });
Object.defineProperty(exports, "not", { enumerable: true, get: function () { return guards_1.not; } });
Object.defineProperty(exports, "select", { enumerable: true, get: function () { return guards_1.select; } });
Object.defineProperty(exports, "instanceOf", { enumerable: true, get: function () { return guards_1.instanceOf; } });
const wildcards_1 = wildcards;
Object.defineProperty(exports, "__", { enumerable: true, get: function () { return wildcards_1.__; } });
/**
 * #### match
 *
 * Entry point to create a pattern matching expression.
 *
 * It returns a `Match` builder, on which you can chain
 * several `.with(pattern, handler)` clauses.
 */
const match = (value) => builder(value, []);
exports.match = match;
/**
 * ### builder
 * This is the implementation of our pattern matching, using the
 * builder pattern.
 */
const builder = (value, cases) => {
    const run = () => {
        const entry = cases.find(({ test }) => test(value));
        if (!entry) {
            let displayedValue;
            try {
                displayedValue = JSON.stringify(value);
            }
            catch (e) {
                displayedValue = value;
            }
            throw new Error(`Pattern matching error: no pattern matches value ${displayedValue}`);
        }
        return entry.handler(entry.select(value), value);
    };
    return {
        with(...args) {
            const handler = args[args.length - 1];
            const patterns = [];
            const predicates = [];
            for (let i = 0; i < args.length - 1; i++) {
                const arg = args[i];
                if (typeof arg === 'function') {
                    predicates.push(arg);
                }
                else {
                    patterns.push(arg);
                }
            }
            let selected = {};
            const doesMatch = (value) => Boolean(patterns.some((pattern) => matchPattern(pattern, value, (key, value) => {
                selected[key] = value;
            })) && predicates.every((predicate) => predicate(value)));
            return builder(value, cases.concat([
                {
                    test: doesMatch,
                    handler,
                    select: (value) => Object.keys(selected).length
                        ? symbols$1.AnonymousSelectKey in selected
                            ? selected[symbols$1.AnonymousSelectKey]
                            : selected
                        : value,
                },
            ]));
        },
        when: (predicate, handler) => builder(value, cases.concat([
            {
                test: predicate,
                handler,
                select: (value) => value,
            },
        ])),
        otherwise: (handler) => builder(value, cases.concat([
            {
                test: () => true,
                handler,
                select: (value) => value,
            },
        ])).run(),
        exhaustive: () => run(),
        run,
    };
};
const isObject = (value) => Boolean(value && typeof value === 'object');
const isGuardPattern = (x) => {
    const pattern = x;
    return pattern && pattern[symbols$1.PatternKind] === symbols$1.Guard;
};
const isNotPattern = (x) => {
    const pattern = x;
    return pattern && pattern[symbols$1.PatternKind] === symbols$1.Not;
};
const isSelectPattern = (x) => {
    const pattern = x;
    return pattern && pattern[symbols$1.PatternKind] === symbols$1.Select;
};
// tells us if the value matches a given pattern.
const matchPattern = (pattern, value, select) => {
    if (isObject(pattern)) {
        if (isGuardPattern(pattern))
            return Boolean(pattern[symbols$1.Guard](value));
        if (isSelectPattern(pattern)) {
            select(pattern[symbols$1.Select], value);
            return true;
        }
        if (isNotPattern(pattern))
            return !matchPattern(pattern[symbols$1.Not], value, select);
        if (!isObject(value))
            return false;
        if (Array.isArray(pattern)) {
            if (!Array.isArray(value))
                return false;
            // List pattern
            if (pattern.length === 1) {
                const selected = {};
                const listSelect = (key, value) => {
                    selected[key] = (selected[key] || []).concat([value]);
                };
                const doesMatch = value.every((v) => matchPattern(pattern[0], v, listSelect));
                if (doesMatch) {
                    Object.keys(selected).forEach((key) => select(key, selected[key]));
                }
                return doesMatch;
            }
            // Tuple pattern
            return pattern.length === value.length
                ? pattern.every((subPattern, i) => matchPattern(subPattern, value[i], select))
                : false;
        }
        if (pattern instanceof Map) {
            if (!(value instanceof Map))
                return false;
            return [...pattern.keys()].every((key) => matchPattern(pattern.get(key), value.get(key), select));
        }
        if (pattern instanceof Set) {
            if (!(value instanceof Set))
                return false;
            if (pattern.size === 0)
                return value.size === 0;
            if (pattern.size === 1) {
                const [subPattern] = [...pattern.values()];
                return Object.values(wildcards_1.__).includes(subPattern)
                    ? matchPattern([subPattern], [...value.values()], select)
                    : value.has(subPattern);
            }
            return [...pattern.values()].every((subPattern) => value.has(subPattern));
        }
        return Object.keys(pattern).every((k) => k in value &&
            matchPattern(
            // @ts-ignore
            pattern[k], 
            // @ts-ignore
            value[k], select));
    }
    return value === pattern;
};
function isMatching(...args) {
    if (args.length === 1) {
        const [pattern] = args;
        return (value) => matchPattern(pattern, value, () => { });
    }
    if (args.length === 2) {
        const [pattern, value] = args;
        return matchPattern(pattern, value, () => { });
    }
    throw new Error(`isMatching wasn't given enough arguments: expected 1 or 2, received ${args.length}.`);
}
exports.isMatching = isMatching;
}(lib));

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __makeTemplateObject$2(cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
}

var __read$2 = function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var styleToObject = function (input) {
    var attributes = input.split(/ ?; ?/);
    return attributes.reduce(function (acc, d) {
        var _a = __read$2(d.split(/ ?: ?/), 2), key = _a[0], value = _a[1];
        if (key && value) {
            acc[key.replace(/-(\w)/g, function (_$0, $1) { return $1.toUpperCase(); })] = Number.isNaN(Number(value))
                ? value
                : Number(value);
        }
        return acc;
    }, {});
};
/* istanbul ignore next */
function randomString$1(length) {
    if (length === void 0) { length = 6; }
    var characters = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    var result = '';
    for (var i = length; i > 0; --i) {
        result += characters[Math.round(Math.random() * (characters.length - 1))];
    }
    return result;
}
var noTextChildNodes = [
    'br',
    'col',
    'colgroup',
    'dl',
    'hr',
    'iframe',
    'img',
    'input',
    'link',
    'menuitem',
    'meta',
    'ol',
    'param',
    'select',
    'table',
    'tbody',
    'tfoot',
    'thead',
    'tr',
    'ul',
    'wbr',
];
/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
// Taken from https://raw.githubusercontent.com/facebook/react/baff5cc2f69d30589a5dc65b089e47765437294b/packages/react-dom/src/shared/possibleStandardNames.js
// tslint:disable:object-literal-sort-keys
var possibleStandardNames = {
    // HTML
    'accept-charset': 'acceptCharset',
    acceptcharset: 'acceptCharset',
    accesskey: 'accessKey',
    allowfullscreen: 'allowFullScreen',
    autocapitalize: 'autoCapitalize',
    autocomplete: 'autoComplete',
    autocorrect: 'autoCorrect',
    autofocus: 'autoFocus',
    autoplay: 'autoPlay',
    autosave: 'autoSave',
    cellpadding: 'cellPadding',
    cellspacing: 'cellSpacing',
    charset: 'charSet',
    class: 'className',
    classid: 'classID',
    classname: 'className',
    colspan: 'colSpan',
    contenteditable: 'contentEditable',
    contextmenu: 'contextMenu',
    controlslist: 'controlsList',
    crossorigin: 'crossOrigin',
    dangerouslysetinnerhtml: 'dangerouslySetInnerHTML',
    datetime: 'dateTime',
    defaultchecked: 'defaultChecked',
    defaultvalue: 'defaultValue',
    enctype: 'encType',
    for: 'htmlFor',
    formmethod: 'formMethod',
    formaction: 'formAction',
    formenctype: 'formEncType',
    formnovalidate: 'formNoValidate',
    formtarget: 'formTarget',
    frameborder: 'frameBorder',
    hreflang: 'hrefLang',
    htmlfor: 'htmlFor',
    httpequiv: 'httpEquiv',
    'http-equiv': 'httpEquiv',
    icon: 'icon',
    innerhtml: 'innerHTML',
    inputmode: 'inputMode',
    itemid: 'itemID',
    itemprop: 'itemProp',
    itemref: 'itemRef',
    itemscope: 'itemScope',
    itemtype: 'itemType',
    keyparams: 'keyParams',
    keytype: 'keyType',
    marginwidth: 'marginWidth',
    marginheight: 'marginHeight',
    maxlength: 'maxLength',
    mediagroup: 'mediaGroup',
    minlength: 'minLength',
    nomodule: 'noModule',
    novalidate: 'noValidate',
    playsinline: 'playsInline',
    radiogroup: 'radioGroup',
    readonly: 'readOnly',
    referrerpolicy: 'referrerPolicy',
    rowspan: 'rowSpan',
    spellcheck: 'spellCheck',
    srcdoc: 'srcDoc',
    srclang: 'srcLang',
    srcset: 'srcSet',
    tabindex: 'tabIndex',
    typemustmatch: 'typeMustMatch',
    usemap: 'useMap',
    // SVG
    accentheight: 'accentHeight',
    'accent-height': 'accentHeight',
    alignmentbaseline: 'alignmentBaseline',
    'alignment-baseline': 'alignmentBaseline',
    allowreorder: 'allowReorder',
    arabicform: 'arabicForm',
    'arabic-form': 'arabicForm',
    attributename: 'attributeName',
    attributetype: 'attributeType',
    autoreverse: 'autoReverse',
    basefrequency: 'baseFrequency',
    baselineshift: 'baselineShift',
    'baseline-shift': 'baselineShift',
    baseprofile: 'baseProfile',
    calcmode: 'calcMode',
    capheight: 'capHeight',
    'cap-height': 'capHeight',
    clippath: 'clipPath',
    'clip-path': 'clipPath',
    clippathunits: 'clipPathUnits',
    cliprule: 'clipRule',
    'clip-rule': 'clipRule',
    colorinterpolation: 'colorInterpolation',
    'color-interpolation': 'colorInterpolation',
    colorinterpolationfilters: 'colorInterpolationFilters',
    'color-interpolation-filters': 'colorInterpolationFilters',
    colorprofile: 'colorProfile',
    'color-profile': 'colorProfile',
    colorrendering: 'colorRendering',
    'color-rendering': 'colorRendering',
    contentscripttype: 'contentScriptType',
    contentstyletype: 'contentStyleType',
    diffuseconstant: 'diffuseConstant',
    dominantbaseline: 'dominantBaseline',
    'dominant-baseline': 'dominantBaseline',
    edgemode: 'edgeMode',
    enablebackground: 'enableBackground',
    'enable-background': 'enableBackground',
    externalresourcesrequired: 'externalResourcesRequired',
    fillopacity: 'fillOpacity',
    'fill-opacity': 'fillOpacity',
    fillrule: 'fillRule',
    'fill-rule': 'fillRule',
    filterres: 'filterRes',
    filterunits: 'filterUnits',
    floodopacity: 'floodOpacity',
    'flood-opacity': 'floodOpacity',
    floodcolor: 'floodColor',
    'flood-color': 'floodColor',
    fontfamily: 'fontFamily',
    'font-family': 'fontFamily',
    fontsize: 'fontSize',
    'font-size': 'fontSize',
    fontsizeadjust: 'fontSizeAdjust',
    'font-size-adjust': 'fontSizeAdjust',
    fontstretch: 'fontStretch',
    'font-stretch': 'fontStretch',
    fontstyle: 'fontStyle',
    'font-style': 'fontStyle',
    fontvariant: 'fontVariant',
    'font-variant': 'fontVariant',
    fontweight: 'fontWeight',
    'font-weight': 'fontWeight',
    glyphname: 'glyphName',
    'glyph-name': 'glyphName',
    glyphorientationhorizontal: 'glyphOrientationHorizontal',
    'glyph-orientation-horizontal': 'glyphOrientationHorizontal',
    glyphorientationvertical: 'glyphOrientationVertical',
    'glyph-orientation-vertical': 'glyphOrientationVertical',
    glyphref: 'glyphRef',
    gradienttransform: 'gradientTransform',
    gradientunits: 'gradientUnits',
    horizadvx: 'horizAdvX',
    'horiz-adv-x': 'horizAdvX',
    horizoriginx: 'horizOriginX',
    'horiz-origin-x': 'horizOriginX',
    imagerendering: 'imageRendering',
    'image-rendering': 'imageRendering',
    kernelmatrix: 'kernelMatrix',
    kernelunitlength: 'kernelUnitLength',
    keypoints: 'keyPoints',
    keysplines: 'keySplines',
    keytimes: 'keyTimes',
    lengthadjust: 'lengthAdjust',
    letterspacing: 'letterSpacing',
    'letter-spacing': 'letterSpacing',
    lightingcolor: 'lightingColor',
    'lighting-color': 'lightingColor',
    limitingconeangle: 'limitingConeAngle',
    markerend: 'markerEnd',
    'marker-end': 'markerEnd',
    markerheight: 'markerHeight',
    markermid: 'markerMid',
    'marker-mid': 'markerMid',
    markerstart: 'markerStart',
    'marker-start': 'markerStart',
    markerunits: 'markerUnits',
    markerwidth: 'markerWidth',
    maskcontentunits: 'maskContentUnits',
    maskunits: 'maskUnits',
    numoctaves: 'numOctaves',
    overlineposition: 'overlinePosition',
    'overline-position': 'overlinePosition',
    overlinethickness: 'overlineThickness',
    'overline-thickness': 'overlineThickness',
    paintorder: 'paintOrder',
    'paint-order': 'paintOrder',
    'panose-1': 'panose1',
    pathlength: 'pathLength',
    patterncontentunits: 'patternContentUnits',
    patterntransform: 'patternTransform',
    patternunits: 'patternUnits',
    pointerevents: 'pointerEvents',
    'pointer-events': 'pointerEvents',
    pointsatx: 'pointsAtX',
    pointsaty: 'pointsAtY',
    pointsatz: 'pointsAtZ',
    preservealpha: 'preserveAlpha',
    preserveaspectratio: 'preserveAspectRatio',
    primitiveunits: 'primitiveUnits',
    refx: 'refX',
    refy: 'refY',
    renderingintent: 'renderingIntent',
    'rendering-intent': 'renderingIntent',
    repeatcount: 'repeatCount',
    repeatdur: 'repeatDur',
    requiredextensions: 'requiredExtensions',
    requiredfeatures: 'requiredFeatures',
    shaperendering: 'shapeRendering',
    'shape-rendering': 'shapeRendering',
    specularconstant: 'specularConstant',
    specularexponent: 'specularExponent',
    spreadmethod: 'spreadMethod',
    startoffset: 'startOffset',
    stddeviation: 'stdDeviation',
    stitchtiles: 'stitchTiles',
    stopcolor: 'stopColor',
    'stop-color': 'stopColor',
    stopopacity: 'stopOpacity',
    'stop-opacity': 'stopOpacity',
    strikethroughposition: 'strikethroughPosition',
    'strikethrough-position': 'strikethroughPosition',
    strikethroughthickness: 'strikethroughThickness',
    'strikethrough-thickness': 'strikethroughThickness',
    strokedasharray: 'strokeDasharray',
    'stroke-dasharray': 'strokeDasharray',
    strokedashoffset: 'strokeDashoffset',
    'stroke-dashoffset': 'strokeDashoffset',
    strokelinecap: 'strokeLinecap',
    'stroke-linecap': 'strokeLinecap',
    strokelinejoin: 'strokeLinejoin',
    'stroke-linejoin': 'strokeLinejoin',
    strokemiterlimit: 'strokeMiterlimit',
    'stroke-miterlimit': 'strokeMiterlimit',
    strokewidth: 'strokeWidth',
    'stroke-width': 'strokeWidth',
    strokeopacity: 'strokeOpacity',
    'stroke-opacity': 'strokeOpacity',
    suppresscontenteditablewarning: 'suppressContentEditableWarning',
    suppresshydrationwarning: 'suppressHydrationWarning',
    surfacescale: 'surfaceScale',
    systemlanguage: 'systemLanguage',
    tablevalues: 'tableValues',
    targetx: 'targetX',
    targety: 'targetY',
    textanchor: 'textAnchor',
    'text-anchor': 'textAnchor',
    textdecoration: 'textDecoration',
    'text-decoration': 'textDecoration',
    textlength: 'textLength',
    textrendering: 'textRendering',
    'text-rendering': 'textRendering',
    underlineposition: 'underlinePosition',
    'underline-position': 'underlinePosition',
    underlinethickness: 'underlineThickness',
    'underline-thickness': 'underlineThickness',
    unicodebidi: 'unicodeBidi',
    'unicode-bidi': 'unicodeBidi',
    unicoderange: 'unicodeRange',
    'unicode-range': 'unicodeRange',
    unitsperem: 'unitsPerEm',
    'units-per-em': 'unitsPerEm',
    unselectable: 'unselectable',
    valphabetic: 'vAlphabetic',
    'v-alphabetic': 'vAlphabetic',
    vectoreffect: 'vectorEffect',
    'vector-effect': 'vectorEffect',
    vertadvy: 'vertAdvY',
    'vert-adv-y': 'vertAdvY',
    vertoriginx: 'vertOriginX',
    'vert-origin-x': 'vertOriginX',
    vertoriginy: 'vertOriginY',
    'vert-origin-y': 'vertOriginY',
    vhanging: 'vHanging',
    'v-hanging': 'vHanging',
    videographic: 'vIdeographic',
    'v-ideographic': 'vIdeographic',
    viewbox: 'viewBox',
    viewtarget: 'viewTarget',
    vmathematical: 'vMathematical',
    'v-mathematical': 'vMathematical',
    wordspacing: 'wordSpacing',
    'word-spacing': 'wordSpacing',
    writingmode: 'writingMode',
    'writing-mode': 'writingMode',
    xchannelselector: 'xChannelSelector',
    xheight: 'xHeight',
    'x-height': 'xHeight',
    xlinkactuate: 'xlinkActuate',
    'xlink:actuate': 'xlinkActuate',
    xlinkarcrole: 'xlinkArcrole',
    'xlink:arcrole': 'xlinkArcrole',
    xlinkhref: 'xlinkHref',
    'xlink:href': 'xlinkHref',
    xlinkrole: 'xlinkRole',
    'xlink:role': 'xlinkRole',
    xlinkshow: 'xlinkShow',
    'xlink:show': 'xlinkShow',
    xlinktitle: 'xlinkTitle',
    'xlink:title': 'xlinkTitle',
    xlinktype: 'xlinkType',
    'xlink:type': 'xlinkType',
    xmlbase: 'xmlBase',
    'xml:base': 'xmlBase',
    xmllang: 'xmlLang',
    'xml:lang': 'xmlLang',
    'xml:space': 'xmlSpace',
    xmlnsxlink: 'xmlnsXlink',
    'xmlns:xlink': 'xmlnsXlink',
    xmlspace: 'xmlSpace',
    ychannelselector: 'yChannelSelector',
    zoomandpan: 'zoomAndPan',
    // event handlers
    onblur: 'onBlur',
    onchange: 'onChange',
    onclick: 'onClick',
    oncontextmenu: 'onContextMenu',
    ondoubleclick: 'onDoubleClick',
    ondrag: 'onDrag',
    ondragend: 'onDragEnd',
    ondragenter: 'onDragEnter',
    ondragexit: 'onDragExit',
    ondragleave: 'onDragLeave',
    ondragover: 'onDragOver',
    ondragstart: 'onDragStart',
    ondrop: 'onDrop',
    onerror: 'onError',
    onfocus: 'onFocus',
    oninput: 'onInput',
    oninvalid: 'onInvalid',
    onkeydown: 'onKeyDown',
    onkeypress: 'onKeyPress',
    onkeyup: 'onKeyUp',
    onload: 'onLoad',
    onmousedown: 'onMouseDown',
    onmouseenter: 'onMouseEnter',
    onmouseleave: 'onMouseLeave',
    onmousemove: 'onMouseMove',
    onmouseout: 'onMouseOut',
    onmouseover: 'onMouseOver',
    onmouseup: 'onMouseUp',
    onscroll: 'onScroll',
    onsubmit: 'onSubmit',
    ontouchcancel: 'onTouchCancel',
    ontouchend: 'onTouchEnd',
    ontouchmove: 'onTouchMove',
    ontouchstart: 'onTouchStart',
    onwheel: 'onWheel',
};

var __assign$1 = function () {
    __assign$1 = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign$1.apply(this, arguments);
};
var __read$1 = function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray$1 = function (to, from) {
    for (var i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i];
    return to;
};
function parseAttributes(node, reactKey) {
    var attributes = {
        key: reactKey,
    };
    /* istanbul ignore else */
    if (node instanceof Element) {
        var nodeClassNames = node.getAttribute('class');
        if (nodeClassNames) {
            attributes.className = nodeClassNames;
        }
        __spreadArray$1([], __read$1(node.attributes)).forEach(function (d) {
            switch (d.name) {
                // this is manually handled above, so break;
                case 'class':
                    break;
                case 'style':
                    attributes[d.name] = styleToObject(d.value);
                    break;
                case 'allowfullscreen':
                case 'allowpaymentrequest':
                case 'async':
                case 'autofocus':
                case 'autoplay':
                case 'checked':
                case 'controls':
                case 'default':
                case 'defer':
                case 'disabled':
                case 'formnovalidate':
                case 'hidden':
                case 'ismap':
                case 'itemscope':
                case 'loop':
                case 'multiple':
                case 'muted':
                case 'nomodule':
                case 'novalidate':
                case 'open':
                case 'readonly':
                case 'required':
                case 'reversed':
                case 'selected':
                case 'typemustmatch':
                    attributes[possibleStandardNames[d.name] || d.name] = true;
                    break;
                default:
                    attributes[possibleStandardNames[d.name] || d.name] = d.value;
            }
        });
    }
    return attributes;
}
function parseChildren(childNodeList, level, options) {
    var children = __spreadArray$1([], __read$1(childNodeList)).map(function (node, index) {
        return convertFromNode(node, __assign$1(__assign$1({}, options), { index: index, level: level + 1 }));
    })
        .filter(Boolean);
    if (!children.length) {
        return null;
    }
    return children;
}
function parseName(nodeName) {
    if (/[a-z]+[A-Z]+[a-z]+/.test(nodeName)) {
        return nodeName;
    }
    return nodeName.toLowerCase();
}
function convertFromNode(input, options) {
    var _a;
    if (options === void 0) { options = {}; }
    if (!input || !(input instanceof Node)) {
        return null;
    }
    var _b = options.actions, actions = _b === void 0 ? [] : _b, _c = options.index, index = _c === void 0 ? 0 : _c, _d = options.level, level = _d === void 0 ? 0 : _d, randomKey = options.randomKey;
    var node = input;
    var key = level + "-" + index;
    var result = [];
    if (randomKey && level === 0) {
        key = randomString$1() + "-" + key;
    }
    /* istanbul ignore else */
    if (Array.isArray(actions)) {
        actions.forEach(function (action) {
            if (action.condition(node, key, level)) {
                if (typeof action.pre === 'function') {
                    node = action.pre(node, key, level);
                    if (!(node instanceof Node)) {
                        node = input;
                        /* istanbul ignore else */
                        if (process.env.NODE_ENV !== 'production') {
                            // eslint-disable-next-line no-console
                            console.warn('The `pre` method always must return a valid DomNode (instanceof Node) - your modification will be ignored (Hint: if you want to render a React-component, use the `post` method instead)');
                        }
                    }
                }
                if (typeof action.post === 'function') {
                    result.push(action.post(node, key, level));
                }
            }
        });
    }
    if (result.length) {
        return result;
    }
    switch (node.nodeType) {
        case 1: {
            // regular dom-node
            return React.createElement(parseName(node.nodeName), parseAttributes(node, key), parseChildren(node.childNodes, level, options));
        }
        case 3: {
            // textnode
            var nodeText = ((_a = node.nodeValue) === null || _a === void 0 ? void 0 : _a.toString()) || '';
            /* istanbul ignore else */
            if (/^\s+$/.test(nodeText) && !/[\u202F\u00A0]/.test(nodeText)) {
                return null;
            }
            /* istanbul ignore next */
            if (!node.parentNode) {
                return nodeText;
            }
            var parentNodeName = node.parentNode.nodeName.toLowerCase();
            if (noTextChildNodes.indexOf(parentNodeName) !== -1) {
                /* istanbul ignore else */
                if (/\S/.test(nodeText)) {
                    // eslint-disable-next-line no-console
                    console.warn("A textNode is not allowed inside '" + parentNodeName + "'. Your text \"" + nodeText + "\" will be ignored");
                }
                return null;
            }
            return nodeText;
        }
        case 8: {
            // html-comment
            return null;
        }
        /* istanbul ignore next */
        default: {
            return null;
        }
    }
}
function convertFromString(input, options) {
    if (options === void 0) { options = {}; }
    if (!input || typeof input !== 'string') {
        return null;
    }
    var _a = options.nodeOnly, nodeOnly = _a === void 0 ? false : _a, _b = options.selector, selector = _b === void 0 ? 'body > *' : _b, _c = options.type, type = _c === void 0 ? 'text/html' : _c;
    try {
        var parser = new DOMParser();
        var doc = parser.parseFromString(input, type);
        var node = doc.querySelector(selector);
        if (!(node instanceof Node)) {
            throw new Error('Error parsing input');
        }
        if (nodeOnly) {
            return node;
        }
        return convertFromNode(node, options);
    }
    catch (error) {
        /* istanbul ignore else */
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.error(error);
        }
    }
    return null;
}
function convert(input, options) {
    if (options === void 0) { options = {}; }
    if (typeof input === 'string') {
        return convertFromString(input, options);
    }
    if (input instanceof Node) {
        return convertFromNode(input, options);
    }
    return null;
}

var exenv = {exports: {}};

/*!
  Copyright (c) 2015 Jed Watson.
  Based on code that is Copyright 2013-2015, Facebook, Inc.
  All rights reserved.
*/

(function (module) {
/* global define */

(function () {

	var canUseDOM = !!(
		typeof window !== 'undefined' &&
		window.document &&
		window.document.createElement
	);

	var ExecutionEnvironment = {

		canUseDOM: canUseDOM,

		canUseWorkers: typeof Worker !== 'undefined',

		canUseEventListeners:
			canUseDOM && !!(window.addEventListener || window.attachEvent),

		canUseViewport: canUseDOM && !!window.screen

	};

	if (module.exports) {
		module.exports = ExecutionEnvironment;
	} else {
		window.ExecutionEnvironment = ExecutionEnvironment;
	}

}());
}(exenv));

var STATUS = {
    FAILED: 'failed',
    LOADED: 'loaded',
    LOADING: 'loading',
    PENDING: 'pending',
    READY: 'ready',
    UNSUPPORTED: 'unsupported',
};
function canUseDOM() {
    return exenv.exports.canUseDOM;
}
function isSupportedEnvironment() {
    return supportsInlineSVG() && typeof window !== 'undefined' && window !== null;
}
function supportsInlineSVG() {
    /* istanbul ignore next */
    if (!document) {
        return false;
    }
    var div = document.createElement('div');
    div.innerHTML = '<svg />';
    return !!div.firstChild && div.firstChild.namespaceURI === 'http://www.w3.org/2000/svg';
}
function randomString(length) {
    var letters = 'abcdefghijklmnopqrstuvwxyz';
    var numbers = '1234567890';
    var charset = "" + letters + letters.toUpperCase() + numbers;
    var randomCharacter = function (character) {
        return character[Math.floor(Math.random() * character.length)];
    };
    var R = '';
    for (var i = 0; i < length; i++) {
        R += randomCharacter(charset);
    }
    return R;
}
/**
 *  Remove properties from an object
 */
function removeProperties(input) {
    var filter = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        filter[_i - 1] = arguments[_i];
    }
    var output = {};
    for (var key in input) {
        /* istanbul ignore else */
        if ({}.hasOwnProperty.call(input, key)) {
            if (!filter.includes(key)) {
                output[key] = input[key];
            }
        }
    }
    return output;
}

var __extends = (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = function (to, from) {
    for (var i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i];
    return to;
};
var cacheStore = Object.create(null);
var InlineSVG = /** @class */ (function (_super) {
    __extends(InlineSVG, _super);
    function InlineSVG(props) {
        var _this = _super.call(this, props) || this;
        _this.isActive = false;
        _this.handleCacheQueue = function (content) {
            /* istanbul ignore else */
            if (typeof content === 'string') {
                _this.handleLoad(content);
                return;
            }
            _this.handleError(content);
        };
        _this.handleLoad = function (content) {
            /* istanbul ignore else */
            if (_this.isActive) {
                _this.setState({
                    content: content,
                    status: STATUS.LOADED,
                }, _this.getElement);
            }
        };
        _this.handleError = function (error) {
            var onError = _this.props.onError;
            var status = error.message === 'Browser does not support SVG' ? STATUS.UNSUPPORTED : STATUS.FAILED;
            /* istanbul ignore else */
            if (_this.isActive) {
                _this.setState({ status: status }, function () {
                    /* istanbul ignore else */
                    if (typeof onError === 'function') {
                        onError(error);
                    }
                });
            }
        };
        _this.request = function () {
            var _a = _this.props, cacheRequests = _a.cacheRequests, fetchOptions = _a.fetchOptions, src = _a.src;
            try {
                if (cacheRequests) {
                    cacheStore[src] = { content: '', status: STATUS.LOADING, queue: [] };
                }
                return fetch(src, fetchOptions)
                    .then(function (response) {
                    var contentType = response.headers.get('content-type');
                    var _a = __read((contentType || '').split(/ ?; ?/), 1), fileType = _a[0];
                    if (response.status > 299) {
                        throw new Error('Not found');
                    }
                    if (!['image/svg+xml', 'text/plain'].some(function (d) { return fileType.indexOf(d) >= 0; })) {
                        throw new Error("Content type isn't valid: " + fileType);
                    }
                    return response.text();
                })
                    .then(function (content) {
                    var currentSrc = _this.props.src;
                    // the current src don't match the previous one, skipping...
                    if (src !== currentSrc) {
                        return;
                    }
                    _this.handleLoad(content);
                    /* istanbul ignore else */
                    if (cacheRequests) {
                        var cache = cacheStore[src];
                        /* istanbul ignore else */
                        if (cache) {
                            cache.content = content;
                            cache.status = STATUS.LOADED;
                            cache.queue = cache.queue.filter(function (cb) {
                                cb(content);
                                return false;
                            });
                        }
                    }
                })
                    .catch(function (error) {
                    _this.handleError(error);
                    /* istanbul ignore else */
                    if (cacheRequests) {
                        var cache = cacheStore[src];
                        /* istanbul ignore else */
                        if (cache) {
                            cache.queue.forEach(function (cb) {
                                cb(error);
                            });
                            delete cacheStore[src];
                        }
                    }
                });
            }
            catch (error) {
                return _this.handleError(new Error(error.message));
            }
        };
        _this.state = {
            content: '',
            element: null,
            hasCache: !!props.cacheRequests && !!cacheStore[props.src],
            status: STATUS.PENDING,
        };
        _this.hash = props.uniqueHash || randomString(8);
        return _this;
    }
    InlineSVG.prototype.componentDidMount = function () {
        this.isActive = true;
        if (!canUseDOM()) {
            return;
        }
        var status = this.state.status;
        var src = this.props.src;
        try {
            /* istanbul ignore else */
            if (status === STATUS.PENDING) {
                /* istanbul ignore else */
                if (!isSupportedEnvironment()) {
                    throw new Error('Browser does not support SVG');
                }
                /* istanbul ignore else */
                if (!src) {
                    throw new Error('Missing src');
                }
                this.load();
            }
        }
        catch (error) {
            this.handleError(error);
        }
    };
    InlineSVG.prototype.componentDidUpdate = function (prevProps, prevState) {
        if (!canUseDOM()) {
            return;
        }
        var _a = this.state, hasCache = _a.hasCache, status = _a.status;
        var _b = this.props, onLoad = _b.onLoad, src = _b.src;
        if (prevState.status !== STATUS.READY && status === STATUS.READY) {
            /* istanbul ignore else */
            if (onLoad) {
                onLoad(src, hasCache);
            }
        }
        if (prevProps.src !== src) {
            if (!src) {
                this.handleError(new Error('Missing src'));
                return;
            }
            this.load();
        }
    };
    InlineSVG.prototype.componentWillUnmount = function () {
        this.isActive = false;
    };
    InlineSVG.prototype.processSVG = function () {
        var content = this.state.content;
        var preProcessor = this.props.preProcessor;
        if (preProcessor) {
            return preProcessor(content);
        }
        return content;
    };
    InlineSVG.prototype.updateSVGAttributes = function (node) {
        var _this = this;
        var _a = this.props, _b = _a.baseURL, baseURL = _b === void 0 ? '' : _b, uniquifyIDs = _a.uniquifyIDs;
        var replaceableAttributes = ['id', 'href', 'xlink:href', 'xlink:role', 'xlink:arcrole'];
        var linkAttributes = ['href', 'xlink:href'];
        var isDataValue = function (name, value) {
            return linkAttributes.indexOf(name) >= 0 && (value ? value.indexOf('#') < 0 : false);
        };
        if (!uniquifyIDs) {
            return node;
        }
        __spreadArray([], __read(node.children)).map(function (d) {
            if (d.attributes && d.attributes.length) {
                var attributes_1 = Object.values(d.attributes).map(function (a) {
                    var attr = a;
                    var match = a.value.match(/url\((.*?)\)/);
                    if (match && match[1]) {
                        attr.value = a.value.replace(match[0], "url(" + baseURL + match[1] + "__" + _this.hash + ")");
                    }
                    return attr;
                });
                replaceableAttributes.forEach(function (r) {
                    var attribute = attributes_1.find(function (a) { return a.name === r; });
                    if (attribute && !isDataValue(r, attribute.value)) {
                        attribute.value = attribute.value + "__" + _this.hash;
                    }
                });
            }
            if (d.children.length) {
                return _this.updateSVGAttributes(d);
            }
            return d;
        });
        return node;
    };
    InlineSVG.prototype.getNode = function () {
        var _a = this.props, description = _a.description, title = _a.title;
        try {
            var svgText = this.processSVG();
            var node = convert(svgText, { nodeOnly: true });
            if (!node || !(node instanceof SVGSVGElement)) {
                throw new Error('Could not convert the src to a DOM Node');
            }
            var svg = this.updateSVGAttributes(node);
            if (description) {
                var originalDesc = svg.querySelector('desc');
                if (originalDesc && originalDesc.parentNode) {
                    originalDesc.parentNode.removeChild(originalDesc);
                }
                var descElement = document.createElement('desc');
                descElement.innerHTML = description;
                svg.prepend(descElement);
            }
            if (title) {
                var originalTitle = svg.querySelector('title');
                if (originalTitle && originalTitle.parentNode) {
                    originalTitle.parentNode.removeChild(originalTitle);
                }
                var titleElement = document.createElement('title');
                titleElement.innerHTML = title;
                svg.prepend(titleElement);
            }
            return svg;
        }
        catch (error) {
            return this.handleError(error);
        }
    };
    InlineSVG.prototype.getElement = function () {
        try {
            var node = this.getNode();
            var element = convert(node);
            if (!element || !React.isValidElement(element)) {
                throw new Error('Could not convert the src to a React element');
            }
            this.setState({
                element: element,
                status: STATUS.READY,
            });
        }
        catch (error) {
            this.handleError(new Error(error.message));
        }
    };
    InlineSVG.prototype.load = function () {
        var _this = this;
        /* istanbul ignore else */
        if (this.isActive) {
            this.setState({
                content: '',
                element: null,
                status: STATUS.LOADING,
            }, function () {
                var _a = _this.props, cacheRequests = _a.cacheRequests, src = _a.src;
                var cache = cacheRequests && cacheStore[src];
                if (cache) {
                    /* istanbul ignore else */
                    if (cache.status === STATUS.LOADING) {
                        cache.queue.push(_this.handleCacheQueue);
                    }
                    else if (cache.status === STATUS.LOADED) {
                        _this.handleLoad(cache.content);
                    }
                    return;
                }
                var dataURI = src.match(/data:image\/svg[^,]*?(;base64)?,(.*)/);
                var inlineSrc;
                if (dataURI) {
                    inlineSrc = dataURI[1] ? atob(dataURI[2]) : decodeURIComponent(dataURI[2]);
                }
                else if (src.indexOf('<svg') >= 0) {
                    inlineSrc = src;
                }
                if (inlineSrc) {
                    _this.handleLoad(inlineSrc);
                    return;
                }
                _this.request();
            });
        }
    };
    InlineSVG.prototype.render = function () {
        var _a = this.state, element = _a.element, status = _a.status;
        var _b = this.props, _c = _b.children, children = _c === void 0 ? null : _c, innerRef = _b.innerRef, _d = _b.loader, loader = _d === void 0 ? null : _d;
        var elementProps = removeProperties(this.props, 'baseURL', 'cacheRequests', 'children', 'description', 'fetchOptions', 'innerRef', 'loader', 'onError', 'onLoad', 'preProcessor', 'src', 'title', 'uniqueHash', 'uniquifyIDs');
        if (!canUseDOM()) {
            return loader;
        }
        if (element) {
            return React.cloneElement(element, __assign({ ref: innerRef }, elementProps));
        }
        if ([STATUS.UNSUPPORTED, STATUS.FAILED].indexOf(status) > -1) {
            return children;
        }
        return loader;
    };
    InlineSVG.defaultProps = {
        cacheRequests: true,
        uniquifyIDs: false,
    };
    return InlineSVG;
}(React.PureComponent));
var SVG = InlineSVG;

var bag = "<svg\n  width=\"19\"\n  height=\"23\"\n  viewBox=\"0 0 19 23\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    fill-rule=\"evenodd\"\n    clip-rule=\"evenodd\"\n    d=\"M9.50016 0.875C6.33147 0.875 3.74638 3.56898 3.74638 6.87114V8.58432H2.10244C1.19416 8.58432 0.458496 9.35097 0.458496 10.2975V20.5766C0.458496 21.5231 1.19416 22.2898 2.10244 22.2898H16.8979C17.8062 22.2898 18.5418 21.5231 18.5418 20.5766V10.2975C18.5418 9.35097 17.8062 8.58432 16.8979 8.58432H15.254V6.87114C15.254 3.67862 12.8168 1.10628 9.79196 0.934961C9.69908 0.898128 9.59962 0.878426 9.50016 0.875ZM9.5003 2.58724C11.7796 2.58724 13.6102 4.49487 13.6102 6.87019V8.58338H5.39045V6.87019C5.39045 4.49487 7.22098 2.58724 9.5003 2.58724Z\"\n  />\n</svg>\n";

var basket = "<svg width=\"20\" height=\"19\" viewBox=\"0 0 20 19\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n  <path\n    d=\"M10 0C9.80327 0 9.60651 0.0754222 9.45613 0.226702L4.29688 5.41667H6.47236L10 1.86802L13.5276 5.41667H15.7031L10.5439 0.226702C10.3935 0.0754222 10.1967 0 10 0ZM0.769231 6.96429C0.344615 6.96429 0 7.31095 0 7.7381V9.28572C0 9.71286 0.344615 10.0595 0.769231 10.0595H0.896935L2.09285 17.2777C2.21669 18.0237 2.85874 18.5714 3.61028 18.5714H16.3882C17.1405 18.5714 17.7818 18.0237 17.9056 17.2777L19.1031 10.0595H19.2308C19.6554 10.0595 20 9.71286 20 9.28572V7.7381C20 7.31095 19.6554 6.96429 19.2308 6.96429H0.769231ZM6.92308 10.0595C7.34846 10.0595 7.69231 10.4054 7.69231 10.8333V15.4762C7.69231 15.9041 7.34846 16.25 6.92308 16.25C6.49769 16.25 6.15385 15.9041 6.15385 15.4762V10.8333C6.15385 10.4054 6.49769 10.0595 6.92308 10.0595ZM10 10.0595C10.4254 10.0595 10.7692 10.4054 10.7692 10.8333V15.4762C10.7692 15.9041 10.4254 16.25 10 16.25C9.57462 16.25 9.23077 15.9041 9.23077 15.4762V10.8333C9.23077 10.4054 9.57462 10.0595 10 10.0595ZM13.0769 10.0595C13.5023 10.0595 13.8462 10.4054 13.8462 10.8333V15.4762C13.8462 15.9041 13.5023 16.25 13.0769 16.25C12.6515 16.25 12.3077 15.9041 12.3077 15.4762V10.8333C12.3077 10.4054 12.6515 10.0595 13.0769 10.0595Z\"\n  /></svg\n>\n";

var burger = "<svg\n  width=\"27\"\n  height=\"16\"\n  viewBox=\"0 0 27 16\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    fill-rule=\"evenodd\"\n    clip-rule=\"evenodd\"\n    d=\"M1.01424 0.000100432C0.653607 -0.00499986 0.318161 0.184476 0.136355 0.495973C-0.0454516 0.807471 -0.0454516 1.19273 0.136355 1.50423C0.318161 1.81573 0.653607 2.0052 1.01424 2.0001H25.0142C25.3749 2.0052 25.7103 1.81573 25.8921 1.50423C26.0739 1.19273 26.0739 0.807471 25.8921 0.495973C25.7103 0.184476 25.3749 -0.00499986 25.0142 0.000100432H1.01424ZM1.01424 7.0001C0.653607 6.995 0.318161 7.18448 0.136355 7.49597C-0.0454516 7.80747 -0.0454516 8.19273 0.136355 8.50423C0.318161 8.81573 0.653607 9.0052 1.01424 9.0001H25.0142C25.3749 9.0052 25.7103 8.81573 25.8921 8.50423C26.0739 8.19273 26.0739 7.80747 25.8921 7.49597C25.7103 7.18448 25.3749 6.995 25.0142 7.0001H1.01424ZM0.136355 14.496C0.318161 14.1845 0.653607 13.995 1.01424 14.0001H25.0142C25.3749 13.995 25.7103 14.1845 25.8921 14.496C26.0739 14.8075 26.0739 15.1927 25.8921 15.5042C25.7103 15.8157 25.3749 16.0052 25.0142 16.0001H1.01424C0.653607 16.0052 0.318161 15.8157 0.136355 15.5042C-0.0454516 15.1927 -0.0454516 14.8075 0.136355 14.496Z\"\n  />\n</svg>\n";

var calendar = "<svg\n  width=\"19px\"\n  height=\"19px\"\n  viewBox=\"0 0 19 19\"\n  version=\"1.1\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n  xmlnsXlink=\"http://www.w3.org/1999/xlink\"\n  fill=\"#A4B1B3\"\n>\n  <g\n    transform=\"translate(-701.000000, -1358.000000)\"\n    fillRule=\"nonzero\"\n  >\n    <g transform=\"translate(504.000000, 1318.000000)\">\n      <g transform=\"translate(197.163218, 40.000000)\">\n        <path\n          d=\"M3.84725415,0 C3.31633308,0 2.88544061,0.4256 2.88544061,0.95 L2.88544061,1.9 L1.92362708,1.9 C0.865632184,1.9 0,2.755 0,3.8 L0,17.1 C0,18.145 0.865632184,19 1.92362708,19 L15.3890166,19 C16.4470115,19 17.3126437,18.145 17.3126437,17.1 L17.3126437,3.8 C17.3126437,2.755 16.4470115,1.9 15.3890166,1.9 L14.4272031,1.9 L14.4272031,0.95 C14.4272031,0.4256 13.9963106,0 13.4653895,0 C12.9344685,0 12.503576,0.4256 12.503576,0.95 L12.503576,1.9 L4.80906769,1.9 L4.80906769,0.95 C4.80906769,0.4256 4.37817522,0 3.84725415,0 Z M1.92362708,6.65 L15.3890166,6.65 L15.3890166,16.15 C15.3890166,16.6744 14.9581241,17.1 14.4272031,17.1 L2.88544061,17.1 C2.35451954,17.1 1.92362708,16.6744 1.92362708,16.15 L1.92362708,6.65 Z\"\n          id=\"Shape\"\n        ></path>\n      </g>\n    </g>\n  </g>\n</svg>";

var celery = "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 30 30\" fill=\"#fc4a5a\">\n  <path\n    d=\"M 15.667969 3 C 14.135969 3 12.836109 4.0171094 12.412109 5.4121094 C 11.017109 5.8361094 10 7.1359688 10 8.6679688 C 10 9.6929688 10.464563 10.639484 11.226562 11.271484 C 11.164563 11.529484 11.132812 11.796406 11.132812 12.066406 C 11.132812 13.852406 12.518484 15.323031 14.271484 15.457031 C 14.449484 15.476031 14.544969 15.577516 14.542969 15.728516 C 14.676969 17.481516 16.146594 18.867188 17.933594 18.867188 C 18.203594 18.867188 18.470516 18.835437 18.728516 18.773438 C 19.361516 19.534437 20.307031 20 21.332031 20 C 22.864031 20 24.163891 18.982891 24.587891 17.587891 C 25.982891 17.163891 27 15.864031 27 14.332031 C 27 13.730031 26.837109 13.144813 26.537109 12.632812 C 26.837109 12.120812 27 11.535594 27 10.933594 C 27 9.9085937 26.535437 8.960125 25.773438 8.328125 C 25.835438 8.070125 25.867188 7.8032031 25.867188 7.5332031 C 25.867188 5.6582031 24.341797 4.1328125 22.466797 4.1328125 C 22.196797 4.1328125 21.929875 4.1645625 21.671875 4.2265625 C 21.038875 3.4655625 20.091406 3 19.066406 3 C 18.464406 3 17.879188 3.1628906 17.367188 3.4628906 C 16.854187 3.1628906 16.269969 3 15.667969 3 z M 9.2246094 13.013672 C 6.9516094 15.061672 5.0765 16.762344 4.6875 17.152344 C 2.4375 19.402344 2.4375 23.064453 4.6875 25.314453 C 5.8125 26.438453 7.2905781 27 8.7675781 27 C 10.245578 27 11.722656 26.438453 12.847656 25.314453 C 13.234656 24.927453 14.940953 23.055297 17.001953 20.779297 C 16.138953 20.626297 15.343969 20.263094 14.667969 19.746094 L 11.707031 22.707031 C 11.512031 22.902031 11.256 23 11 23 C 10.744 23 10.487969 22.902031 10.292969 22.707031 C 9.9019687 22.316031 9.9019688 21.683969 10.292969 21.292969 L 13.332031 18.253906 C 13.126031 17.916906 12.943453 17.565547 12.814453 17.185547 C 12.434453 17.056547 12.083094 16.873969 11.746094 16.667969 L 8.7070312 19.707031 C 8.5120313 19.902031 8.256 20 8 20 C 7.744 20 7.4879688 19.902031 7.2929688 19.707031 C 6.9019687 19.316031 6.9019687 18.683969 7.2929688 18.292969 L 10.253906 15.332031 C 9.7399063 14.661031 9.3786094 13.871672 9.2246094 13.013672 z\"\n  />\n</svg>\n";

var cereals = "<svg\n  xmlns=\"http://www.w3.org/2000/svg\"\n  fill=\"#fc4a5a\"\n  viewBox=\"0 0 30 30\"\n  width=\"30px\"\n  height=\"30px\"\n>\n  <path\n    d=\"M 27 3 C 27 3 24.000547 3.0003125 22.560547 4.3203125 C 21.000547 6.0003125 21.479844 7.8001562 21.839844 8.1601562 C 22.199844 8.4001563 24.120781 9.0003125 25.800781 7.3203125 C 27.000781 6.0003125 27 3 27 3 z M 19.919922 4.3203125 C 18.599922 4.4403125 16.559219 5.7594531 16.199219 7.4394531 C 15.599219 9.5994531 16.919297 11.040156 17.279297 11.160156 C 17.639297 11.280156 18.960547 10.439766 19.560547 8.7597656 C 20.400547 6.3597656 20.039922 4.3203125 19.919922 4.3203125 z M 14.265625 8.1601562 C 14.265625 8.1601562 11.865234 9.4791406 11.865234 12.119141 C 11.865234 14.759141 12.944922 15.359375 13.544922 15.359375 C 14.144922 15.359375 15.464844 14.160234 15.464844 12.240234 C 15.464844 9.4802344 14.265625 8.1601563 14.265625 8.1601562 z M 24.359375 9.9003906 C 23.549375 9.9003906 22.440234 10.019453 21.240234 10.439453 C 19.440234 11.159453 18.719844 12.360703 18.839844 12.720703 C 18.959844 13.080703 20.400547 14.400781 22.560547 13.800781 C 24.360547 13.320781 25.559687 11.400078 25.679688 10.080078 C 25.679688 10.020078 25.169375 9.9003906 24.359375 9.9003906 z M 9.8339844 12.710938 C 9.8339844 12.710938 7.4335937 14.031875 7.4335938 16.671875 C 7.4335938 19.311875 8.5132812 19.910156 9.1132812 19.910156 C 9.7132812 19.910156 11.033203 18.711016 11.033203 16.791016 C 11.033203 14.031016 9.8339844 12.710938 9.8339844 12.710938 z M 17.710938 14.552734 C 15.790938 14.552734 14.591797 15.872656 14.591797 16.472656 C 14.591797 17.072656 15.190078 18.152344 17.830078 18.152344 C 20.470078 18.152344 21.791016 15.751953 21.791016 15.751953 C 21.791016 15.751953 20.470938 14.552734 17.710938 14.552734 z M 5.4003906 17.160156 C 5.4003906 17.160156 3 18.479141 3 21.119141 C 3 23.759141 4.0796875 24.359375 4.6796875 24.359375 C 5.2796875 24.359375 6.5996094 23.160234 6.5996094 21.240234 C 6.5996094 18.480234 5.4003906 17.160156 5.4003906 17.160156 z M 13.232422 18.910156 C 11.312422 18.910156 10.111328 20.230078 10.111328 20.830078 C 10.111328 21.430078 10.711563 22.509766 13.351562 22.509766 C 15.991563 22.509766 17.3125 20.109375 17.3125 20.109375 C 17.3125 20.109375 15.992422 18.910156 13.232422 18.910156 z M 8.8808594 23.400391 C 6.9608594 23.400391 5.7597656 24.720312 5.7597656 25.320312 C 5.7597656 25.920313 6.36 27 9 27 C 11.64 27 12.960938 24.599609 12.960938 24.599609 C 12.960938 24.599609 11.640859 23.400391 8.8808594 23.400391 z\"\n  />\n</svg>\n";

var chevronDown = "<svg\n  width=\"12\"\n  height=\"8\"\n  viewBox=\"0 0 12 8\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    d=\"M11.4422 0.000167963C11.2985 0.00390542 11.1619 0.0699598 11.0623 0.183942L5.99882 5.75334L0.93536 0.183942C0.832777 0.067955 0.691915 0.00251849 0.544799 0.00250906C0.323079 0.00257316 0.123501 0.150393 0.040069 0.376342C-0.0433626 0.602292 0.00598957 0.861315 0.16488 1.03141L5.61358 7.02454C5.82637 7.25849 6.17127 7.25849 6.38406 7.02454L11.8328 1.03141C11.9947 0.860106 12.0445 0.597028 11.9581 0.36889C11.8717 0.140753 11.6668 -0.00566251 11.4422 0.000167963Z\"\n  />\n</svg>\n";

var chevronLeft = "<svg\n  width=\"9\"\n  height=\"17\"\n  viewBox=\"0 0 9 17\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    d=\"M8.99979 0.790216C8.99512 0.99382 8.91255 1.18727 8.77007 1.32843L1.80833 8.50167L8.77007 15.6749C8.91506 15.8202 8.99685 16.0198 8.99686 16.2282C8.99678 16.5423 8.81201 16.825 8.52957 16.9432C8.24713 17.0614 7.92336 16.9915 7.71074 16.7664L0.21933 9.04743C-0.0731105 8.74598 -0.0731105 8.25736 0.21933 7.95591L7.71074 0.236919C7.92487 0.0074787 8.25372 -0.0630188 8.53889 0.0593853C8.82406 0.181787 9.00708 0.471994 8.99979 0.790216Z\"\n  />\n</svg>\n";

var chevronRight = "<svg\n  width=\"9\"\n  height=\"17\"\n  viewBox=\"0 0 9 17\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    d=\"M0.000209245 16.2098C0.00488107 16.0062 0.0874491 15.8127 0.229926 15.6716L7.19167 8.49833L0.229927 1.32509C0.0849437 1.17977 0.00314807 0.980213 0.00313629 0.771799C0.00321642 0.457695 0.187991 0.174959 0.470428 0.0567644C0.752865 -0.0614303 1.07664 0.00848527 1.28926 0.23358L8.78067 7.95257C9.07311 8.25402 9.07311 8.74264 8.78067 9.04409L1.28926 16.7631C1.07513 16.9925 0.746284 17.063 0.461112 16.9406C0.175941 16.8182 -0.00707886 16.528 0.000209245 16.2098Z\"\n  />\n</svg>\n";

var chevronUp = "<svg\n  width=\"18\"\n  height=\"10\"\n  viewBox=\"0 0 18 10\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    d=\"M16.2323 9.28616C16.0284 9.28134 15.8347 9.19615 15.6933 9.04914L8.51013 1.86594L1.32693 9.04914C1.1814 9.19873 0.981573 9.28313 0.77287 9.28314C0.45833 9.28306 0.175202 9.09241 0.0568432 8.80098C-0.0615156 8.50956 0.008497 8.17549 0.233904 7.95611L7.96361 0.226399C8.26548 -0.0753441 8.75477 -0.0753441 9.05664 0.226399L16.7863 7.95611C17.0161 8.17704 17.0867 8.51635 16.9641 8.8106C16.8416 9.10484 16.5509 9.29368 16.2323 9.28616Z\"\n  />\n</svg>\n";

var close = "<svg\n  width=\"14\"\n  height=\"14\"\n  viewBox=\"0 0 14 14\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    fill-rule=\"evenodd\"\n    clip-rule=\"evenodd\"\n    d=\"M1.33448 0.000349994C1.67327 0.000349994 2.01765 0.131943 2.28084 0.39373L7 5.11289L11.7192 0.39373C12.2385 -0.131243 13.0869 -0.131243 13.6063 0.39373C14.1312 0.913104 14.1312 1.76146 13.6063 2.28084L8.88711 7L13.6063 11.7192C14.1312 12.2385 14.1312 13.0869 13.6063 13.6063C13.0869 14.1312 12.2385 14.1312 11.7192 13.6063L7 8.88711L2.28084 13.6063C1.76146 14.1312 0.913104 14.1312 0.39373 13.6063C-0.131243 13.0869 -0.131243 12.2385 0.39373 11.7192L5.11289 7L0.39373 2.28084C-0.131243 1.76146 -0.131243 0.913104 0.39373 0.39373C0.651317 0.131943 0.9957 0.000349994 1.33448 0.000349994Z\"\n  />\n</svg>\n";

var crustaceans = "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 30 30\" fill=\"#fc4a5a\">\n  <path\n    style=\"\n      line-height: normal;\n      text-indent: 0;\n      text-align: start;\n      text-decoration-line: none;\n      text-decoration-style: solid;\n      text-decoration-color: #000;\n      text-transform: none;\n      isolation: auto;\n      mix-blend-mode: normal;\n    \"\n    d=\"M 11 3 A 1 1 0 0 0 10 4 A 1 1 0 0 0 11 5 A 1 1 0 0 0 12 4 A 1 1 0 0 0 11 3 z M 15 3 A 1.0001 1.0001 0 1 0 15 5 L 24 5 C 25.116666 5 26 5.8833339 26 7 C 26 8.1166661 25.116666 9 24 9 L 22 9 L 12.306641 9 C 10.158641 9 8.1363437 9.7298906 6.5273438 10.962891 C 6.1833437 11.226891 6.1422187 11.729156 6.4492188 12.035156 L 6.6699219 12.255859 C 7.0779219 12.663859 7.099875 13.354187 6.671875 13.742188 C 6.481875 13.914187 6.24 14 6 14 C 5.744 14 5.4879688 13.902031 5.2929688 13.707031 L 5.2148438 13.628906 C 4.8818437 13.295906 4.3197969 13.370203 4.0917969 13.783203 C 3.6987969 14.495203 3.40075 15.266031 3.21875 16.082031 C 3.11475 16.553031 3.4750313 17 3.9570312 17 C 4.4410312 17 4.8874688 17.323828 4.9804688 17.798828 C 5.1054688 18.439828 4.619 19 4 19 L 3.9375 19 C 3.4595 19 3.1012656 19.439203 3.1972656 19.908203 C 3.4422656 21.097203 3.9215625 22.173469 4.6015625 23.105469 C 4.7815625 23.351469 5.1289531 23.401609 5.3769531 23.224609 C 5.6389531 23.037609 6.0101562 22.887938 6.4101562 23.085938 C 6.9581563 23.356938 7.147625 24.011516 6.765625 24.603516 C 6.620625 24.828516 6.7056406 25.131391 6.9316406 25.275391 C 7.6706406 25.748391 8.6321875 26.172984 9.6171875 26.458984 C 9.8561875 26.527984 10.119922 26.472922 10.294922 26.294922 C 10.474922 26.113922 10.723 26 11 26 C 11.384 26 11.708094 26.219062 11.871094 26.539062 C 11.990094 26.772063 12.242906 26.911828 12.503906 26.923828 C 12.693906 26.932828 12.885078 26.9375 13.080078 26.9375 C 13.321078 26.9385 14.216047 26.944172 15.373047 26.951172 C 15.682047 26.953172 15.980953 26.797391 16.126953 26.525391 C 16.293953 26.213391 16.62 26 17 26 C 17.386 26 17.717812 26.220016 17.882812 26.541016 C 18.022813 26.814016 18.321906 26.970656 18.628906 26.972656 C 20.901906 26.986656 23 27 23 27 C 24.657 27 26 25.657 26 24 L 26 23 C 26 22.448 25.552 22 25 22 L 20 22 C 18.895 22 18 22.895 18 24 L 13.126953 24 C 11.500953 24 10.045531 22.674828 10.019531 21.048828 C 9.9935313 19.369828 11.346531 18 13.019531 18 L 17 18 C 20.866 18 24 14.866 24 11 C 26.061893 11 27.718364 9.3940305 27.921875 7.3828125 A 1.0001 1.0001 0 0 0 28 7 C 28 4.8026661 26.197334 3 24 3 L 15 3 z M 19.130859 11 C 19.958859 11 20.630859 11.672 20.630859 12.5 C 20.630859 13.328 19.958859 14 19.130859 14 C 18.302859 14 17.630859 13.328 17.630859 12.5 C 17.630859 11.672 18.301859 11 19.130859 11 z\"\n    font-weight=\"400\"\n    font-family=\"sans-serif\"\n    white-space=\"normal\"\n    overflow=\"visible\"\n  />\n</svg>\n";

var eggs = "<svg\n  xmlns=\"http://www.w3.org/2000/svg\"\n  fill=\"#fc4a5a\"\n  viewBox=\"0 0 30 30\"\n  width=\"30px\"\n  height=\"30px\"\n>\n  <path\n    d=\"M 10 4 C 6.125 4 2 10.757031 2 16.832031 C 2 22.907031 5.582 26 10 26 C 14.418 26 18 22.907031 18 16.832031 C 18 10.757031 13.875 4 10 4 z M 18.771484 10.449219 C 19.551484 12.526219 20 14.751031 20 16.832031 C 20 20.824031 18.555047 24.024938 16.123047 25.960938 C 21.929047 25.563937 28 21.672 28 18 C 28 14.887 23.636484 11.616219 18.771484 10.449219 z M 11 14 C 11.552 14 12 14.448 12 15 C 12 15.552 11.552 16 11 16 C 10.448 16 10 15.552 10 15 C 10 14.448 10.448 14 11 14 z M 12.5 19 C 13.328 19 14 19.672 14 20.5 C 14 21.328 13.328 22 12.5 22 C 11.672 22 11 21.328 11 20.5 C 11 19.672 11.672 19 12.5 19 z M 23 19 C 23.552 19 24 19.448 24 20 C 24 20.552 23.552 21 23 21 C 22.448 21 22 20.552 22 20 C 22 19.448 22.448 19 23 19 z\"\n  />\n</svg>\n";

var euro = "<svg\n  width=\"12\"\n  height=\"16\"\n  viewBox=\"-3 0 20 15\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    d=\"M9.016 14.3209C7.5 14.303 6.571 13.9476 5.818 13.2009C5.108 12.496 4.73 11.496 4.66 10.2213H10V8.66578H4.625V7.11111H10V5.55556H4.688C4.786 4.37156 5.158 3.43289 5.836 2.768C6.576 2.04178 7.631 1.67911 9.002 1.67911C9.921 1.67911 10.837 1.80444 11.748 2.05422L12 0.329778C10.871 0.110222 9.864 0 8.978 0C6.947 0 5.335 0.512 4.144 1.53511C3.024 2.49689 2.414 3.848 2.273 5.55556H0V7.11111H2.25V8.66667H0V10.2222H2.255C2.348 12.0204 2.951 13.432 4.094 14.4338C5.285 15.4782 6.917 16 8.99 16C9.993 16 10.996 15.8924 12 15.6773L11.749 13.9573C10.897 14.2 9.985 14.3209 9.016 14.3209Z\"\n  />\n</svg>\n";

var eye = "<svg width=\"21\" height=\"14\" viewBox=\"0 0 21 14\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n<path d=\"M10.5 0C4.25719 0 0.224692 6.44783 0.151758 6.56523C0.0535874 6.68889 0.000106136 6.84211 0 7C0.000124182 7.13545 0.039543 7.26795 0.113477 7.38145C0.114383 7.38282 0.115295 7.38418 0.116211 7.38555C0.128247 7.41038 3.51318 14 10.5 14C17.4572 14 20.8371 7.47512 20.8783 7.39512C20.8811 7.39059 20.8838 7.38603 20.8865 7.38145C20.9605 7.26795 20.9999 7.13545 21 7C21 6.84274 20.947 6.69007 20.8496 6.5666C20.8492 6.56614 20.8487 6.56569 20.8482 6.56523C20.7753 6.44783 16.7428 0 10.5 0ZM10.5 2.1C13.2062 2.1 15.4 4.2938 15.4 7C15.4 9.7062 13.2062 11.9 10.5 11.9C7.7938 11.9 5.6 9.7062 5.6 7C5.6 4.2938 7.7938 2.1 10.5 2.1ZM10.5 4.9C9.94305 4.9 9.4089 5.12125 9.01508 5.51508C8.62125 5.9089 8.4 6.44305 8.4 7C8.4 7.55695 8.62125 8.0911 9.01508 8.48492C9.4089 8.87875 9.94305 9.1 10.5 9.1C11.057 9.1 11.5911 8.87875 11.9849 8.48492C12.3788 8.0911 12.6 7.55695 12.6 7C12.6 6.44305 12.3788 5.9089 11.9849 5.51508C11.5911 5.12125 11.057 4.9 10.5 4.9Z\"/>\n</svg>\n";

var filter = "<svg width=\"21\" height=\"20\" viewBox=\"0 0 21 20\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n<path fill-rule=\"evenodd\" clip-rule=\"evenodd\" d=\"M14.8699 0C13.7398 0 12.7767 0.732397 12.4123 1.73554H0.886216C0.572457 1.73207 0.2788 1.89521 0.117987 2.16595C-0.0393291 2.43756 -0.0393291 2.76905 0.117987 3.04066C0.2788 3.3114 0.572457 3.47455 0.886216 3.47107H12.4123C12.7767 4.47421 13.7398 5.20661 14.8699 5.20661C16 5.20661 16.9631 4.47421 17.3275 3.47107H20.1138C20.4275 3.47455 20.7212 3.3114 20.882 3.04066C21.0393 2.76905 21.0393 2.43756 20.882 2.16595C20.7212 1.89521 20.4275 1.73207 20.1138 1.73554H17.3275C16.9631 0.732397 16 0 14.8699 0ZM14.8699 1.73554C15.3445 1.73554 15.7063 2.09132 15.7334 2.55558C15.7299 2.58595 15.7299 2.62066 15.7334 2.6545C15.7063 3.11876 15.3445 3.47107 14.8699 3.47107C14.3953 3.47107 14.0335 3.11529 14.0064 2.65103C14.0099 2.62066 14.0099 2.58595 14.0064 2.55211C14.0335 2.08785 14.3953 1.73554 14.8699 1.73554ZM7.87806 6.94215C6.748 6.94215 5.78488 7.67455 5.42043 8.67769H0.886216C0.572457 8.67421 0.2788 8.83736 0.117987 9.1081C-0.0393291 9.37971 -0.0393291 9.7112 0.117987 9.98281C0.2788 10.2536 0.572457 10.4167 0.886216 10.4132H5.42043C5.78488 11.4164 6.748 12.1488 7.87806 12.1488C9.00812 12.1488 9.97124 11.4164 10.3357 10.4132H20.1138C20.4275 10.4167 20.7212 10.2536 20.882 9.98281C21.0393 9.7112 21.0393 9.37971 20.882 9.1081C20.7212 8.83736 20.4275 8.67421 20.1138 8.67769H10.3357C9.97124 7.67455 9.00812 6.94215 7.87806 6.94215ZM7.87806 8.67769C8.35263 8.67769 8.71446 9.03347 8.74155 9.49426C8.73806 9.5281 8.73806 9.56281 8.74155 9.59665C8.71446 10.0609 8.35263 10.4132 7.87806 10.4132C7.40349 10.4132 7.04166 10.0574 7.01457 9.59665C7.01806 9.56281 7.01806 9.5281 7.01457 9.49426C7.04166 9.03 7.40349 8.67769 7.87806 8.67769ZM11.374 13.8843C10.2439 13.8843 9.2808 14.6167 8.91635 15.6198H0.886216C0.572457 15.6164 0.2788 15.7795 0.117987 16.0502C-0.0393291 16.3219 -0.0393291 16.6533 0.117987 16.925C0.2788 17.1957 0.572457 17.3588 0.886216 17.3554H8.91635C9.2808 18.3585 10.2439 19.0909 11.374 19.0909C12.504 19.0909 13.4672 18.3585 13.8316 17.3554H20.1138C20.4275 17.3588 20.7212 17.1957 20.882 16.925C21.0393 16.6533 21.0393 16.3219 20.882 16.0502C20.7212 15.7795 20.4275 15.6164 20.1138 15.6198H13.8316C13.4672 14.6167 12.504 13.8843 11.374 13.8843ZM11.374 15.6198C11.8486 15.6198 12.2104 15.9756 12.2375 16.4364C12.234 16.4702 12.234 16.505 12.2375 16.5388C12.2104 17.0031 11.8486 17.3554 11.374 17.3554C10.8994 17.3554 10.5376 16.9996 10.5105 16.5388C10.514 16.505 10.514 16.4702 10.5105 16.4364C10.5376 15.9721 10.8994 15.6198 11.374 15.6198Z\" />\n</svg>\n";

var fish = "<svg\n  xmlns=\"http://www.w3.org/2000/svg\"\n  fill=\"#fc4a5a\"\n  viewBox=\"0 0 30 30\"\n  width=\"30px\"\n  height=\"30px\"\n>\n  <path\n    d=\"M26,14.811C25.178,12.242,23.303,4,8,4C7.447,4,7,4.447,7,5c0,0.976,1.936,2.276,1.936,4.962 C7.518,11.289,7.953,13,6.503,13C5.221,13,4.946,10,4,10c-0.354,0-0.681,0.187-0.86,0.49C3.093,10.569,2,12.444,2,15 s1.093,4.431,1.14,4.51C3.319,19.813,3.646,20,4,20c1.003,0,1.505-3,2.503-3S7.546,18.607,9,20c0,1.113-1,3.892-1,5 c0,0.553,0.447,1,1,1c12.389,0,19-7.351,19-9.298C28,16.005,26.423,15.396,26,14.811z M21,16c-0.552,0-1-0.448-1-1 c0-0.552,0.448-1,1-1s1,0.448,1,1C22,15.552,21.552,16,21,16z\"\n  />\n</svg>\n";

var halal = "<svg\n  width=\"60\"\n  height=\"65\"\n  viewBox=\"0 0 80 60\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    fill-rule=\"evenodd\"\n    clip-rule=\"evenodd\"\n    d=\"M40.0271 -6C18.1968 -6 0.431641 11.0493 0.431641 32C0.431641 52.9507 18.1968 70 40.0271 70C61.8573 70 79.6225 52.9507 79.6225 32C79.6225 11.0493 61.8573 -6 40.0271 -6ZM26.0803 4.99609C26.0803 4.99609 27.2069 8.87224 28.7742 9.73991C28.7742 9.73991 27.6464 11.4754 27.6464 14.4837C27.6464 15.4806 27.8206 17.4254 28.0208 19.6591L28.0208 19.6592L28.0208 19.6593C28.4247 24.1666 28.934 29.8502 28.3295 31.2943C27.4254 33.4539 23.6061 38.8294 18.2244 38.6172C12.8196 38.3829 10.5702 35.373 10.3492 32.3457C10.1281 29.3564 11.6775 26.575 13.7068 24.2064C13.4296 24.7542 10.28 31.1653 13.507 33.2363C16.8627 35.377 22.7023 34.9327 25.6357 32.3519C28.5492 29.7869 24.0696 8.44727 24.0696 8.44727C24.0696 8.44727 25.6382 6.07593 26.0803 4.99609ZM45.7884 10.5825C45.7884 10.5825 45.8053 10.397 45.782 10.3289C45.7119 10.1248 45.3027 10.9981 43.1719 21.27C40.331 34.95 25.082 38.3651 25.082 38.3651C33.5818 39.6571 39.6892 35.586 41.8637 31.3205C44.0414 27.0391 45.55 18.0539 45.55 18.0539C45.55 30.8979 46.7141 34.8451 49.9774 36.3302C53.2407 37.8344 59.5628 35.6901 64.1361 33.9738C67.0464 32.894 70.4582 33.3429 70.4582 33.3429L73.0232 29.0939C73.0232 29.0939 69.3692 29.4991 66.7526 28.4383C65.9875 28.123 65.1302 27.6265 64.2074 27.0922C61.9701 25.7967 59.3483 24.2785 56.7249 24.5789C53.3526 24.9684 51.4311 30.4905 51.1374 31.4627C51.3024 31.1112 51.9468 30.209 54.1213 29.0753C56.9391 27.5712 62.6023 30.7762 62.6023 30.7762C60.0022 33.129 55.1892 33.3405 51.2792 32.9162C47.7133 32.5164 47.5267 24.8349 47.3717 18.456L47.3717 18.4556L47.3717 18.4552L47.3717 18.4547L47.3717 18.4543L47.3717 18.4538L47.3717 18.4533L47.3717 18.4528L47.3716 18.4522C47.356 17.8084 47.3407 17.1779 47.3222 16.5695C47.0814 9.93215 45.7884 10.5825 45.7884 10.5825ZM28.8648 13.8418C28.8648 13.8418 39.1346 16.9887 37.8808 27.9928C37.8808 27.9928 36.6265 23.0757 34.3234 21.4987C33.5419 20.9692 32.6912 20.6642 31.8986 20.38C30.3354 19.8195 28.9984 19.3401 28.8648 17.3796C28.6636 14.4251 28.8648 13.8418 28.8648 13.8418ZM20.0161 42.1484H17.709V50.999H20.0097V47.4242H23.6444V50.999H25.9516C25.9516 50.999 25.9483 42.1484 25.9516 42.1484H23.6444V45.6182H20.0161V42.1484ZM31.1335 42.1484H33.9498L37.0174 50.999H34.4846L33.9304 49.0508H30.9659L30.373 50.999H28.0723L31.1335 42.1484ZM41.4573 42.1484H39.1309V50.999H45.3112V49.1374H41.4573V42.1484ZM50.5514 42.1484H53.3741L56.4288 50.999H53.9026L53.329 49.0508H50.3645L49.7909 50.999H47.4902L50.5514 42.1484ZM61.7972 42.1484H59.4707V50.999H65.651V49.1374H61.7972V42.1484ZM32.4042 44.1523L31.4117 47.4427H33.4933L32.5138 44.1523H32.4042ZM50.8025 47.4427L51.8014 44.1523H51.9303L52.9034 47.4427H50.8025Z\"\n  />\n</svg>\n";

var heart = "<svg\n  width=\"13\"\n  height=\"12\"\n  viewBox=\"0 0 13 10\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    fill-rule=\"evenodd\"\n    clip-rule=\"evenodd\"\n    d=\"M3.8871 0.571533C2.15396 0.571533 0.75 1.96389 0.75 3.68271C0.75 6.67129 4.2484 9.53013 5.88044 10.8013C5.88235 10.8032 5.88427 10.8032 5.88619 10.8051C5.89721 10.8146 5.91206 10.8255 5.92356 10.835V10.8331C6.09031 10.9572 6.29204 11.0261 6.5 11.0261C6.70796 11.0261 6.90969 10.9591 7.07644 10.8331V10.835C7.08027 10.8331 7.0841 10.8293 7.08794 10.8255C7.08938 10.8236 7.09321 10.8217 7.09704 10.8198C7.09896 10.8179 7.10279 10.8146 7.10663 10.8108C8.73483 9.54486 12.25 6.67889 12.25 3.68271C12.25 1.96389 10.846 0.571533 9.1129 0.571533C7.45833 0.571533 6.5 1.99715 6.5 1.99715C6.5 1.99715 5.54167 0.571533 3.8871 0.571533Z\"\n  />\n</svg>\n";

var highProtein = "<svg\n  xmlns=\"http://www.w3.org/2000/svg\"\n  xmlns:xlink=\"http://www.w3.org/1999/xlink\"\n  width=\"30pt\"\n  height=\"30pt\"\n  viewBox=\"0 0 30 30\"\n  version=\"1.1\"\n>\n  <g id=\"surface1294249\">\n    <path\n      style=\"\n        stroke: none;\n        fill-rule: nonzero;\n        fill: rgb(34.901961%, 35.294118%, 51.764706%);\n        fill-opacity: 1;\n      \"\n      d=\"M 14.984375 0.988281 C 14.679688 0.992188 14.394531 1.136719 14.207031 1.375 C 14.207031 1.378906 14.203125 1.378906 14.203125 1.382812 L 9.292969 6.292969 C 9.03125 6.542969 8.925781 6.917969 9.019531 7.265625 C 9.109375 7.617188 9.382812 7.890625 9.734375 7.980469 C 10.082031 8.074219 10.457031 7.96875 10.707031 7.707031 L 14 4.414062 L 14 28 C 13.996094 28.359375 14.183594 28.695312 14.496094 28.878906 C 14.808594 29.058594 15.191406 29.058594 15.503906 28.878906 C 15.816406 28.695312 16.003906 28.359375 16 28 L 16 4.414062 L 19.292969 7.707031 C 19.542969 7.96875 19.917969 8.074219 20.265625 7.980469 C 20.617188 7.890625 20.890625 7.617188 20.980469 7.265625 C 21.074219 6.917969 20.96875 6.542969 20.707031 6.292969 L 15.792969 1.375 C 15.597656 1.125 15.300781 0.980469 14.984375 0.984375 Z M 14.984375 0.988281 \"\n    />\n  </g>\n</svg>\n";

var individual = "<svg\n  width=\"15\"\n  height=\"16\"\n  viewBox=\"0 0 15 16\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    fill-rule=\"evenodd\"\n    clip-rule=\"evenodd\"\n    d=\"M2.5 0.989746C1.11929 0.989746 0 2.10903 0 3.48974V13.4576C0 14.8383 1.11929 15.9576 2.5 15.9576H12.5C13.8807 15.9576 15 14.8383 15 13.4576V3.48975C15 2.10903 13.8807 0.989746 12.5 0.989746H2.5ZM1 3.48974C1 2.66132 1.67157 1.98975 2.5 1.98975H12.5C13.3284 1.98975 14 2.66132 14 3.48975V13.4576C14 14.2861 13.3284 14.9576 12.5 14.9576H2.5C1.67157 14.9576 1 14.2861 1 13.4576V3.48974ZM2 5.18128C2 3.96857 2.9845 2.98633 4.2 2.98633H10.8C12.0155 2.98633 13 3.96857 13 5.18128V11.7661C13 12.9789 12.0155 13.9611 10.8 13.9611H4.2C2.9845 13.9611 2 12.9789 2 11.7661V5.18128Z\"\n  />\n</svg>\n";

var info = "<svg\n  width=\"14\"\n  height=\"14\"\n  viewBox=\"0 0 14 14\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    fill-rule=\"evenodd\"\n    clip-rule=\"evenodd\"\n    d=\"M7 0C3.13425 0 0 3.13425 0 7C0 10.8658 3.13425 14 7 14C10.8658 14 14 10.8658 14 7C14 3.13425 10.8658 0 7 0ZM6.41667 10.5V6.41667H7.58333V10.5H6.41667ZM6.125 4.08333C6.125 4.56633 6.517 4.95833 7 4.95833C7.483 4.95833 7.875 4.56633 7.875 4.08333C7.875 3.60033 7.483 3.20833 7 3.20833C6.517 3.20833 6.125 3.60033 6.125 4.08333Z\"\n  />\n</svg>\n";

var knifeFork = "<svg width=\"12\" height=\"14\" viewBox=\"0 0 12 14\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n<path fill-rule=\"evenodd\" clip-rule=\"evenodd\" d=\"M0 0.583333C0 0.26075 0.26075 0 0.583333 0C0.905917 0 1.16667 0.26075 1.16667 0.583333V3.5C1.16667 3.822 1.428 4.08333 1.75 4.08333C2.072 4.08333 2.33333 3.822 2.33333 3.5V0.583333C2.33333 0.26075 2.59408 0 2.91667 0C3.23925 0 3.5 0.26075 3.5 0.583333V3.5C3.5 3.822 3.76133 4.08333 4.08333 4.08333C4.40533 4.08333 4.66667 3.822 4.66667 3.5V0.583333C4.66667 0.26075 4.92742 0 5.25 0C5.57258 0 5.83333 0.26075 5.83333 0.583333V4.08333C5.83333 5.57169 5.16977 6.12948 4.59059 6.61635C4.1124 7.01831 3.69174 7.37192 3.74951 8.16097C3.79155 8.73513 3.84172 9.31018 3.89012 9.86494C3.9857 10.9605 4.07437 11.977 4.07992 12.7513C4.08202 12.7786 4.08316 12.806 4.08333 12.8333C4.08333 13.4777 3.561 14 2.91667 14C2.27233 14 1.75 13.4777 1.75 12.8333C1.75 12.0555 1.8416 11.0135 1.94052 9.88826C1.98997 9.32576 2.04124 8.74247 2.08382 8.16097C2.1416 7.37192 1.72093 7.01831 1.24275 6.61635C0.663562 6.12948 0 5.57169 0 4.08333V0.583333ZM8.16602 0.583333C8.16602 0.261167 8.42718 0 8.74935 0C8.76876 0.000171714 8.78815 0.00131246 8.80745 0.00341798C10.391 0.0346786 11.666 1.32569 11.666 2.91667V7C11.666 7.64458 11.1381 8.16667 10.4993 8.16667C9.8606 8.16667 9.81164 8.58698 10.0356 9.78223C10.0664 9.94658 10.098 10.1121 10.1296 10.2772C10.3017 11.1779 10.4719 12.0684 10.4914 12.708C10.4963 12.7496 10.4989 12.7914 10.4993 12.8333C10.4993 13.4777 9.97701 14 9.33268 14C8.68835 14 8.16602 13.4777 8.16602 12.8333V0.583333Z\"/>\n</svg>\n";

var leaf = "<svg\n  width=\"14\"\n  height=\"12\"\n  viewBox=\"0 0 14 12\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    d=\"M7.36402 0C4.63674 0 1.36402 1.63636 1.36402 4.90909C1.36402 7.26968 3.0559 8.20142 4.005 8.54297C2.11576 10.2179 0.413732 10.6523 0.413732 10.6523C0.121311 10.7253 -0.0565984 11.0215 0.0163597 11.3139C0.0893177 11.6063 0.385516 11.7843 0.677936 11.7113C0.677936 11.7113 2.68968 11.1996 4.79868 9.30895C4.79927 9.31066 4.79914 9.31149 4.79974 9.31321C5.14774 9.03612 5.47454 8.75507 5.75109 8.47798L10.2511 3.97798C10.2511 3.97798 10.4759 5.29474 9.38497 6.38565L6.52133 9.24929C6.16351 9.60711 5.73928 9.96712 5.28873 10.3168C5.82982 11.1502 6.78692 12 8.45492 12C11.7277 12 13.364 9.63655 13.364 5.45455V0H7.36402Z\"\n  />\n</svg>\n";

var locationPin = "<svg width=\"16\" height=\"20\" viewBox=\"0 0 16 20\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n  <path\n    fill-rule=\"evenodd\"\n    clip-rule=\"evenodd\"\n    d=\"M8 0C3.58057 0 0 3.68762 0 8.23917C0 13.6594 5.20114 16.1794 5.85257 16.782C6.51771 17.3976 7.06743 18.6347 7.29029 19.4209C7.39771 19.7976 7.70057 19.9906 8 20C8.29943 19.9906 8.60229 19.7976 8.70971 19.4209C8.93257 18.6347 9.48229 17.3976 10.1474 16.782C10.7989 16.1794 16 13.6594 16 8.23917C16 3.68762 12.4194 0 8 0ZM8 5.88512C9.26286 5.88512 10.2857 6.93856 10.2857 8.23917C10.2857 9.53978 9.26286 10.5932 8 10.5932C6.73714 10.5932 5.71429 9.53978 5.71429 8.23917C5.71429 6.93856 6.73714 5.88512 8 5.88512Z\"\n  />\n</svg>\n";

var lowCarb = "<svg\n  xmlns=\"http://www.w3.org/2000/svg\"\n  xmlns:xlink=\"http://www.w3.org/1999/xlink\"\n  width=\"30pt\"\n  height=\"30pt\"\n  viewBox=\"0 0 30 30\"\n  version=\"1.1\"\n>\n  <g id=\"surface1123404\">\n    <path\n      style=\"\n        stroke: none;\n        fill-rule: nonzero;\n        fill: rgb(97.254902%, 64.313725%, 28.235294%);\n        fill-opacity: 1;\n      \"\n      d=\"M 14.984375 0.988281 C 14.433594 0.996094 13.992188 1.449219 14 2 L 14 25.585938 L 10.707031 22.292969 C 10.519531 22.097656 10.261719 21.992188 9.992188 21.988281 C 9.582031 21.992188 9.21875 22.238281 9.0625 22.613281 C 8.910156 22.992188 9 23.421875 9.292969 23.707031 L 14.207031 28.617188 C 14.394531 28.867188 14.6875 29.011719 15 29.011719 C 15.308594 29.015625 15.601562 28.871094 15.792969 28.625 C 15.792969 28.621094 15.796875 28.621094 15.796875 28.617188 L 20.707031 23.707031 C 20.96875 23.457031 21.074219 23.082031 20.980469 22.734375 C 20.890625 22.382812 20.617188 22.109375 20.265625 22.019531 C 19.917969 21.925781 19.542969 22.03125 19.292969 22.292969 L 16 25.585938 L 16 2 C 16.003906 1.730469 15.898438 1.46875 15.707031 1.277344 C 15.515625 1.085938 15.253906 0.980469 14.984375 0.988281 Z M 14.984375 0.988281 \"\n    />\n  </g>\n</svg>\n";

var lupin = "<svg\n  xmlns=\"http://www.w3.org/2000/svg\"\n  xmlns:xlink=\"http://www.w3.org/1999/xlink\"\n  xml:space=\"preserve\"\n  version=\"1.0\"\n  style=\"\n    shape-rendering: geometricPrecision;\n    text-rendering: geometricPrecision;\n    image-rendering: optimizeQuality;\n  \"\n  viewBox=\"0 0 10000 12500\"\n  x=\"0px\"\n  y=\"0px\"\n  fill-rule=\"evenodd\"\n  clip-rule=\"evenodd\"\n>\n  <g>\n    <path\n      class=\"fil0\"\n      d=\"M3014 6866c319,264 762,427 1254,427 855,0 1373,-357 1593,-818 104,-219 143,-462 119,-701 -24,-240 -111,-477 -258,-683 -57,-80 -123,-155 -199,-223 -39,43 -80,84 -124,124 -311,282 -707,432 -1106,444 -400,13 -805,-111 -1136,-377 -56,-45 -111,-96 -164,-151 -36,31 -71,63 -103,97 -235,240 -375,544 -375,873 0,384 191,733 499,988zm3105 -4086c-148,-40 -295,-63 -439,-71 10,33 20,64 32,95 119,305 344,546 619,693 276,148 603,201 925,132 10,-2 21,-5 32,-7 -21,-36 -44,-71 -69,-105 -239,-338 -625,-610 -1100,-737zm-2491 1365c-6,-60 -8,-120 -7,-181 2,-126 19,-252 52,-375 131,-490 488,-869 958,-1084 458,-209 1024,-263 1591,-111 567,152 1031,482 1322,892 300,421 419,927 288,1418 -92,344 -296,634 -571,850 -244,191 -545,325 -876,389 -4,242 -58,483 -163,704 -281,590 -920,1046 -1954,1046 -587,0 -1120,-198 -1508,-519 -399,-329 -645,-787 -645,-1296 0,-437 183,-838 489,-1151 263,-270 618,-475 1024,-582z\"\n    />\n  </g>\n</svg>\n";

var milk = "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 30 30\" fill=\"#fc4a5a\">\n  <path\n    style=\"\n      line-height: normal;\n      text-indent: 0;\n      text-align: start;\n      text-decoration-line: none;\n      text-decoration-style: solid;\n      text-decoration-color: #000;\n      text-transform: none;\n      isolation: auto;\n      mix-blend-mode: normal;\n    \"\n    d=\"M 9 3 A 1.0001 1.0001 0 0 0 8 4 L 8 5.0546875 L 5.1601562 9.4570312 A 1.0001 1.0001 0 0 0 5 10 L 5 26 A 1.0001 1.0001 0 0 0 6 27 L 22 27 A 1.0001 1.0001 0 0 0 23 26 L 23 10 A 1.0001 1.0001 0 0 0 22.839844 9.4570312 L 20 5.0566406 L 20 4 A 1.0001 1.0001 0 0 0 19 3 L 9 3 z M 9.7714844 6 L 17.994141 6 L 16 9 L 7.8359375 9 L 9.7714844 6 z M 7 11 L 16 11 L 16 25 L 7 25 L 7 11 z\"\n    font-weight=\"400\"\n    font-family=\"sans-serif\"\n    white-space=\"normal\"\n    overflow=\"visible\"\n  />\n</svg>\n";

var minus = "<svg\n  width=\"16\"\n  height=\"4\"\n  viewBox=\"0 0 16 4\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    fill-rule=\"evenodd\"\n    clip-rule=\"evenodd\"\n    d=\"M6.83413 0.787228H2.10685C1.68065 0.781214 1.28421 1.00463 1.06935 1.37192C0.854488 1.7392 0.854488 2.19347 1.06935 2.56076C1.28421 2.92804 1.68065 3.15146 2.10685 3.14544H6.83413H9.19776H13.925C14.3512 3.15146 14.7477 2.92804 14.9625 2.56076C15.1774 2.19347 15.1774 1.7392 14.9625 1.37192C14.7477 1.00463 14.3512 0.781214 13.925 0.787228H9.19776H6.83413Z\"\n  />\n</svg>\n";

var molluscs = "<svg\n  xmlns=\"http://www.w3.org/2000/svg\"\n  fill=\"#fc4a5a\"\n  viewBox=\"0 0 30 30\"\n  width=\"30px\"\n  height=\"30px\"\n>\n  <path\n    d=\"M 15 3 C 13.841 3 12.871938 3.5571406 12.335938 4.6191406 C 12.043938 5.1981406 11.282813 5.84025 10.757812 6.03125 C 10.232812 6.22225 8.982 6 8.5 6 C 7.119 6 6 7.1697812 6 8.5507812 C 6 9.0437813 6.3094219 10.564344 5.8574219 11.152344 C 5.4054219 11.739344 4.5700469 12.034609 4.3730469 12.099609 C 3.5760469 12.362609 3 13.114 3 14 C 3 14.446 3.1514375 14.8545 3.3984375 15.1875 C 4.2994375 16.6975 6.163 18.602781 7 19.300781 C 7 19.300781 8.8664375 19.565531 11.023438 19.769531 C 11.144438 17.669531 12.87 16 15 16 C 17.13 16 18.855563 17.669531 18.976562 19.769531 C 21.133563 19.565531 23 19.300781 23 19.300781 C 23.837 18.602781 25.700563 16.6975 26.601562 15.1875 C 26.848562 14.8545 27 14.446 27 14 C 27 13.114 26.423953 12.362609 25.626953 12.099609 C 25.429953 12.034609 24.594578 11.740344 24.142578 11.152344 C 23.690578 10.565344 24 9.0437812 24 8.5507812 C 24 7.1697812 22.881 6 21.5 6 C 21.018 6 19.767188 6.22225 19.242188 6.03125 C 18.717188 5.84025 17.956062 5.1981406 17.664062 4.6191406 C 17.128062 3.5571406 16.159 3 15 3 z M 5.0019531 21 C 4.2589531 21 3.7744219 21.782266 4.1074219 22.447266 C 4.8894219 24.012266 10 26 15 26 C 20 26 25.110578 24.012266 25.892578 22.447266 C 26.225578 21.782266 25.741047 21 24.998047 21 C 24.998047 21 20 22 15 22 C 10 22 5.0019531 21 5.0019531 21 z\"\n  />\n</svg>\n";

var mustard = "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 30 30\" fill=\"#fc4a5a\">\n  <path\n    d=\"M 15 0 C 14.756 0 14.547812 0.17696875 14.507812 0.41796875 L 13.910156 4 L 12 4 C 11.448 4 11 4.448 11 5 L 11 7 L 19 7 L 19 5 C 19 4.448 18.552 4 18 4 L 16.089844 4 L 15.492188 0.41796875 C 15.452187 0.17696875 15.244 3.7007434e-17 15 0 z M 12 9 C 10.343 9 9 10.343 9 12 L 9 14 L 10 14 C 11.105 14 12 14.895 12 16 L 12 22 C 12 23.105 11.105 24 10 24 L 9 24 L 9 26 C 9 27.105 9.895 28 11 28 L 19 28 C 20.105 28 21 27.105 21 26 L 21 12 C 21 10.343 19.657 9 18 9 L 12 9 z\"\n  />\n</svg>\n";

var noGluten = "<svg\n  width=\"52\"\n  height=\"65\"\n  viewBox=\"0 0 75 60\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    fill-rule=\"evenodd\"\n    clip-rule=\"evenodd\"\n    d=\"M4.52454 -3L0.767578 0.605578L75.1595 72L78.9164 68.3944L51.2426 41.8357C55.6818 40.252 57.9418 36.4173 57.9418 36.4173C57.9418 36.4173 54.4346 33.3596 47.1017 33.3596C45.6138 33.3596 44.299 33.6494 43.1735 34.0916L40.309 31.3426C40.7941 30.2404 41.1134 28.9454 41.1134 27.4731C41.1134 20.4357 37.9272 17.0697 37.9272 17.0697C37.9272 17.0697 33.8696 19.2749 32.2451 23.6036L4.52454 -3ZM71.6229 4C71.6229 4 63.7001 4.0008 59.8965 7.38348C55.7759 11.6887 57.0419 16.3011 57.9928 17.2236C58.9437 17.8386 64.0177 19.3766 68.4553 15.0714C71.625 11.6887 71.6229 4 71.6229 4ZM53.4448 8C50.0531 8.29776 44.8094 11.571 43.8844 15.7397C42.3427 21.0994 45.7347 24.6743 46.6597 24.972C47.5847 25.2698 50.9797 23.1845 52.5214 19.0158C54.6798 13.0606 53.7532 8 53.4448 8ZM65.084 22C62.9902 22 60.1231 22.2937 57.0211 23.3295C52.3682 25.1053 50.506 28.068 50.8162 28.9559C51.1264 29.8438 54.8506 33.0996 60.4341 31.6198C65.087 30.4359 68.1867 25.6988 68.4969 22.4432C68.4969 22.2952 67.1778 22 65.084 22ZM24.8336 30C22.9995 31.5846 20.5645 34.4836 20.5645 38.705C20.5645 45.4634 23.3773 47 24.9405 47C26.5036 47 29.9423 43.9302 29.9423 39.015C29.9423 37.3152 29.7528 35.8424 29.4793 34.565L24.8336 30ZM14.3142 40C14.3142 40 8.06055 43.2978 8.06055 49.8985C8.06055 56.4993 10.8734 58 12.4365 58C13.9997 58 17.4384 55.0018 17.4384 50.2013C17.4384 43.3006 14.3142 40 14.3142 40ZM35.164 45C30.0288 45 26.8164 48.3002 26.8164 49.8003C26.8164 51.3005 28.4218 54 35.4826 54C39.8555 54 42.8639 51.7044 44.5301 49.9517L39.6877 45.4249C38.3799 45.1748 36.8864 45 35.164 45ZM23.4845 56C18.4838 56 15.3555 59.3002 15.3555 60.8003C15.3555 62.3005 16.9188 65 23.7948 65C30.6708 65 34.1112 58.9984 34.1112 58.9984C34.1112 58.9984 30.673 56 23.4845 56Z\"\n  />\n</svg>\n";

var noMilk = "<svg\n  width=\"55\"\n  height=\"65\"\n  viewBox=\"0 0 75 60\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    fill-rule=\"evenodd\"\n    clip-rule=\"evenodd\"\n    d=\"M4.39758 -2L0.640625 1.60558L75.0325 73L78.7895 69.3944L60.9699 52.2928V22.751C60.9699 22.2614 60.8232 21.7771 60.5496 21.3665L52.9993 10.1365V7.45219C52.9993 6.04215 51.8117 4.90239 50.3425 4.90239H23.7739C22.3047 4.90239 21.1171 6.04215 21.1171 7.45219V10.1365L19.5188 12.512L4.39758 -2ZM25.8178 12.5527H47.6901L42.3712 20.2021H27.5302L23.3633 16.2031L25.8178 12.5527ZM13.681 21.2031L13.5668 21.3675C13.5137 21.4491 13.49 21.5447 13.4475 21.6314C13.405 21.7105 13.3652 21.7863 13.3333 21.8705C13.2244 22.1535 13.1465 22.446 13.1465 22.7519V63.5487C13.1465 64.9588 14.3341 66.0985 15.8034 66.0985H58.313C58.9161 66.0985 59.4468 65.8696 59.8905 65.5458L42.3719 48.738V60.9989H18.4602V25.7898L13.681 21.2031ZM32.8438 25.3008H42.3711V34.4442L32.8438 25.3008Z\"\n  />\n</svg>\n";

var none = "<svg\n  width=\"29\"\n  height=\"29\"\n  viewBox=\"0 0 29 29\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    d=\"M14.5 0C6.496 0 0 6.496 0 14.5C0 22.504 6.496 29 14.5 29C22.504 29 29 22.504 29 14.5C29 6.496 22.504 0 14.5 0ZM2.9 14.5C2.9 8.091 8.091 2.9 14.5 2.9C17.1825 2.9 19.6475 3.8135 21.605 5.3505L5.3505 21.605C3.8135 19.6475 2.9 17.1825 2.9 14.5ZM14.5 26.1C11.8175 26.1 9.3525 25.1865 7.395 23.6495L23.6495 7.395C25.1865 9.3525 26.1 11.8175 26.1 14.5C26.1 20.909 20.909 26.1 14.5 26.1Z\"\n  />\n</svg>\n";

var noNuts = "<svg\n  width=\"51\"\n  height=\"65\"\n  viewBox=\"0 0 75 60\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    fill-rule=\"evenodd\"\n    clip-rule=\"evenodd\"\n    d=\"M43.9625 -2.99893C43.2907 -2.97991 42.653 -2.71319 42.1847 -2.25526L38.5279 1.21836C37.0738 2.59966 36.255 4.47489 36.255 6.4288V7.52271L19.3652 11.8983L4.46537 -2.25526L0.808594 1.21836L73.2167 70L76.8735 66.5264L60.9736 51.4228C63.3259 47.7305 64.7011 43.4176 64.7011 38.7853V28.9594C64.7011 27.6034 63.5425 26.5029 62.1151 26.5029H34.7398L29.5678 21.5899H62.8929C65.3186 21.5899 67.2871 19.7177 67.2871 17.411C67.2871 15.5269 65.9581 13.8771 64.0445 13.3809L41.427 7.52271V6.4288C41.427 5.77884 41.6993 5.15306 42.1847 4.69199L45.8414 1.21836C46.6063 0.512145 46.8362 -0.567664 46.4205 -1.50075C46.0048 -2.43384 45.0287 -3.02879 43.9625 -2.99893ZM15.5665 26.502C14.139 26.502 12.9805 27.6025 12.9805 28.9584V38.7844C12.9805 51.5247 23.1881 61.9999 36.2596 63.2293C36.2568 63.2692 36.2552 63.3092 36.2545 63.3493C36.2545 64.7059 37.4123 65.8057 38.8405 65.8057C40.2687 65.8057 41.4265 64.7059 41.4265 63.3493C41.4261 63.3044 41.4244 63.2596 41.4215 63.2149C46.1156 62.767 50.4368 61.1266 54.0535 58.6186L20.2435 26.502H15.5665Z\"\n  />\n</svg>\n";

var noSugar = "<svg\n  width=\"51\"\n  height=\"65\"\n  viewBox=\"0 0 75 60\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    fill-rule=\"evenodd\"\n    clip-rule=\"evenodd\"\n    d=\"M0.808594 0.605578L4.56555 -3L21.394 13.1504C21.6518 11.2884 22.9095 9.66437 24.7825 9.01195L38.0668 4.3755C39.2411 3.96753 40.5302 3.96765 41.6992 4.37052L54.9835 9.01195C57.0744 9.7412 58.481 11.6661 58.481 13.8028V28.4542C58.481 29.0126 58.3684 29.5502 58.1904 30.0628L70.9246 34.51C73.0156 35.2392 74.4221 37.1641 74.4221 39.3008V53.9522C74.4221 55.9487 73.1978 57.77 71.3034 58.5936L69.5443 59.3606L78.9575 68.3944L75.2005 72L0.808594 0.605578ZM51.7043 13.2906L39.8834 9.16211L28.0625 13.2906L39.8834 17.4141L51.7043 13.2906ZM26.6973 18.2402L37.2261 21.9106V28.3448L26.6973 18.2402ZM21.2849 28.4534V27.3379L31.7618 37.3877L23.9417 34.6586L12.1208 38.7871L23.9417 42.9106L33.8945 39.4395L42.5397 47.7363V53.9514L53.1671 58.5879V57.9355L59.3111 63.8269L58.0242 64.3847C57.3254 64.6907 56.5784 64.8429 55.8292 64.8429C55.0773 64.8429 54.3252 64.6932 53.6238 64.3847L40.3447 58.5929C40.2451 58.5489 40.1578 58.4883 40.0704 58.4277C40.011 58.3864 39.9515 58.3451 39.888 58.309C39.8258 58.3449 39.7677 58.3847 39.7098 58.4244L39.7098 58.4244C39.6195 58.4863 39.5298 58.5478 39.4262 58.5929L26.1419 64.3847C25.4432 64.6907 24.6961 64.8429 23.9469 64.8429C23.195 64.8429 22.4429 64.6932 21.7415 64.3847L8.46244 58.5929C6.5681 57.7718 5.34375 55.9479 5.34375 53.9514V39.3C5.34375 37.1633 6.7503 35.2384 8.84125 34.5092L21.5807 30.062C21.4026 29.5495 21.2849 29.0118 21.2849 28.4534ZM55.8249 34.6602L67.6458 38.7886L55.8249 42.9121L50.4541 41.0396L47.0137 37.7378L55.8249 34.6602ZM21.2856 47.4083L10.6582 43.7031V53.9521L21.2856 58.5886V47.4083Z\"\n  />\n</svg>\n";

var note = "<svg\n  width=\"12\"\n  height=\"12\"\n  viewBox=\"0 0 12 12\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    fill-rule=\"evenodd\"\n    clip-rule=\"evenodd\"\n    d=\"M1.63636 0C0.739107 0 0 0.739107 0 1.63636V10.3636C0 11.2609 0.739107 12 1.63636 12H8.45455C8.5992 12 8.73792 11.9425 8.8402 11.8402L11.8402 8.8402C11.9425 8.73792 12 8.5992 12 8.45455V1.63636C12 0.739107 11.2609 0 10.3636 0H1.63636ZM1.63636 1.09091H10.3636C10.6718 1.09091 10.9091 1.32817 10.9091 1.63636V8.18182H9.27273C8.67 8.18182 8.18182 8.67 8.18182 9.27273V10.9091H1.63636C1.32817 10.9091 1.09091 10.6718 1.09091 10.3636V1.63636C1.09091 1.32817 1.32817 1.09091 1.63636 1.09091ZM2.79388 4.08866C2.89305 3.91875 3.07602 3.8154 3.27273 3.81818H5.45455C5.65126 3.8154 5.83423 3.91875 5.93339 4.08866C6.03256 4.25857 6.03256 4.46871 5.93339 4.63862C5.83423 4.80852 5.65126 4.91187 5.45455 4.90909H3.27273C3.07602 4.91187 2.89305 4.80852 2.79388 4.63862C2.69471 4.46871 2.69471 4.25857 2.79388 4.08866ZM7.63636 3.81818C7.43965 3.8154 7.25668 3.91875 7.15752 4.08866C7.05835 4.25857 7.05835 4.46871 7.15752 4.63862C7.25668 4.80852 7.43965 4.91187 7.63636 4.90909H8.72727C8.92398 4.91187 9.10695 4.80852 9.20612 4.63862C9.30529 4.46871 9.30529 4.25857 9.20612 4.08866C9.10695 3.91875 8.92398 3.8154 8.72727 3.81818H7.63636ZM2.79388 6.27048C2.89305 6.10057 3.07602 5.99722 3.27273 6H7.63636C7.83307 5.99722 8.01604 6.10057 8.11521 6.27048C8.21438 6.44038 8.21438 6.65053 8.11521 6.82043C8.01604 6.99034 7.83307 7.09369 7.63636 7.09091H3.27273C3.07602 7.09369 2.89305 6.99034 2.79388 6.82043C2.69471 6.65053 2.69471 6.44038 2.79388 6.27048Z\"\n  />\n</svg>\n";

var nuts = "<svg\n  xmlns=\"http://www.w3.org/2000/svg\"\n  fill=\"#fc4a5a\"\n  viewBox=\"0 0 30 30\"\n  width=\"30px\"\n  height=\"30px\"\n>\n  <path\n    d=\"M 16.980469 -0.009765625 A 1.0001 1.0001 0 0 0 16.292969 0.29296875 L 14.878906 1.7070312 C 14.316602 2.269336 14 3.0327162 14 3.828125 L 14 4.2734375 L 5.2539062 6.6582031 C 4.5139062 6.8602031 4 7.5318281 4 8.2988281 C 4 9.2378281 4.7621719 10 5.7011719 10 L 24.298828 10 C 25.237828 10 26 9.2378281 26 8.2988281 C 26 7.5318281 25.486094 6.8602031 24.746094 6.6582031 L 16 4.2734375 L 16 3.828125 C 16 3.5635338 16.105273 3.308789 16.292969 3.1210938 L 17.707031 1.7070312 A 1.0001 1.0001 0 0 0 16.980469 -0.009765625 z M 6 12 C 5.448 12 5 12.448 5 13 L 5 17 C 5 22.186388 8.9472641 26.450678 14.001953 26.951172 A 1 1 0 0 0 14 27 A 1 1 0 0 0 15 28 A 1 1 0 0 0 16 27 A 1 1 0 0 0 15.998047 26.951172 C 21.052736 26.450678 25 22.186388 25 17 L 25 13 C 25 12.448 24.552 12 24 12 L 6 12 z\"\n  />\n</svg>\n";

var order = "<svg width=\"19\" height=\"21\" viewBox=\"0 0 19 21\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n<path d=\"M1.88468 0.599609V17.3996H17.4209V0.599609L15.8673 1.39961L14.3137 0.599609L12.76 1.39961L11.2064 0.599609L9.6528 1.39961L8.09917 0.599609L6.54555 1.39961L4.99193 0.599609L3.4383 1.39961L1.88468 0.599609ZM17.4209 17.3996V18.9996H1.88468V17.3996C1.0263 17.3996 0.331055 18.1156 0.331055 18.9996C0.331055 19.8836 1.0263 20.5996 1.88468 20.5996H17.4209C18.2793 20.5996 18.9745 19.8836 18.9745 18.9996C18.9745 18.1156 18.2793 17.3996 17.4209 17.3996ZM5.76874 4.59961H13.5369C13.9657 4.59961 14.3137 4.95801 14.3137 5.39961C14.3137 5.84121 13.9657 6.19961 13.5369 6.19961H5.76874C5.33994 6.19961 4.99193 5.84121 4.99193 5.39961C4.99193 4.95801 5.33994 4.59961 5.76874 4.59961ZM5.76874 9.39961H10.4296C10.8584 9.39961 11.2064 9.75801 11.2064 10.1996C11.2064 10.6412 10.8584 10.9996 10.4296 10.9996H5.76874C5.33994 10.9996 4.99193 10.6412 4.99193 10.1996C4.99193 9.75801 5.33994 9.39961 5.76874 9.39961ZM13.5369 9.39961C13.9657 9.39961 14.3137 9.75801 14.3137 10.1996C14.3137 10.6412 13.9657 10.9996 13.5369 10.9996C13.1081 10.9996 12.76 10.6412 12.76 10.1996C12.76 9.75801 13.1081 9.39961 13.5369 9.39961ZM5.76874 12.5996H8.87598C9.30478 12.5996 9.6528 12.958 9.6528 13.3996C9.6528 13.8412 9.30478 14.1996 8.87598 14.1996H5.76874C5.33994 14.1996 4.99193 13.8412 4.99193 13.3996C4.99193 12.958 5.33994 12.5996 5.76874 12.5996ZM13.5369 12.5996C13.9657 12.5996 14.3137 12.958 14.3137 13.3996C14.3137 13.8412 13.9657 14.1996 13.5369 14.1996C13.1081 14.1996 12.76 13.8412 12.76 13.3996C12.76 12.958 13.1081 12.5996 13.5369 12.5996Z\"/>\n</svg>";

var peanuts = "<svg\n  xmlns=\"http://www.w3.org/2000/svg\"\n  fill=\"#fc4a5a\"\n  viewBox=\"0 0 30 30\"\n  width=\"30px\"\n  height=\"30px\"\n>\n  <path\n    d=\"M 14.5 3 C 12.699 3 11.100656 3.866125 10.097656 5.203125 C 9.4086562 6.122125 9.057 7.943 8 9 C 6.943 10.057 5.4694687 10.423625 4.4804688 11.140625 C 2.9774687 12.231625 2 14.001 2 16 C 2 16.523 2.0683125 17.030672 2.1953125 17.513672 C 2.3673125 18.163672 3.0376875 18.531078 3.6796875 18.330078 C 12.536687 15.564078 14.848937 9.0784687 14.960938 8.7304688 C 15.212938 7.9414688 15.679219 7.9719375 15.949219 8.0859375 C 16.219219 8.1999375 16.359172 8.6145781 16.201172 9.1425781 C 16.086172 9.5265781 14.312969 16.722234 5.0429688 19.990234 C 4.5299688 20.171234 4.4724531 20.87725 4.9394531 21.15625 C 5.8354531 21.69225 6.881 22 8 22 C 15 22 20 14 20 8.5 C 20 5.462 17.538 3 14.5 3 z M 21.779297 15.15625 C 21.310594 15.157 20.772844 15.228156 20.214844 15.285156 C 19.249844 17.224156 17.921344 19.078172 16.277344 20.576172 C 19.686344 21.287172 22.251125 20.766656 22.453125 20.722656 C 22.992125 20.604656 23.525578 20.945422 23.642578 21.482422 C 23.761578 22.021422 23.422766 22.553828 22.884766 22.673828 C 22.624766 22.731828 18.887094 23.501188 14.246094 22.117188 C 12.552094 23.186187 10.628672 23.866844 8.5136719 23.964844 C 12.756672 27.718844 20.1235 27.708641 24.1875 25.431641 C 26.8375 23.947641 27.781875 20.595313 26.296875 17.945312 C 25.416875 16.374312 23.879656 15.402641 22.222656 15.181641 C 22.083406 15.163141 21.935531 15.156 21.779297 15.15625 z\"\n  />\n</svg>\n";

var person = "<svg\n  width=\"11\"\n  height=\"11\"\n  viewBox=\"0 0 11 11\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    fill-rule=\"evenodd\"\n    clip-rule=\"evenodd\"\n    d=\"M2.56937 2.84585C2.56937 1.27399 3.81732 0 5.35704 0C6.89677 0 8.14471 1.27399 8.14471 2.84585V3.32016C8.14471 4.89202 6.89677 6.16601 5.35704 6.16601C3.81732 6.16601 2.56937 4.89202 2.56937 3.32016V2.84585ZM0.419635 9.52878C1.1068 8.61668 3.4949 7.58893 5.35614 7.58893C7.21737 7.58893 9.60639 8.61716 10.2935 9.52878C10.7182 10.0927 10.314 10.9091 9.6175 10.9091H1.09568C0.399228 10.9091 -0.00502062 10.0927 0.419635 9.52878Z\"\n  />\n</svg>\n";

var plant = "<svg\n  width=\"76\"\n  height=\"65\"\n  viewBox=\"0 0 120 50\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    d=\"M98.2792 -10C78.6507 -10 59.0222 -0.507118 59.0222 23.2251C59.0222 28.6598 59.8466 32.8841 61.3433 36.2018C60.3471 38.4659 59.4639 40.692 58.6591 42.8801C56.8386 39.1351 54.6156 35.6275 52.0247 32.3667C51.0874 31.1801 50.0912 30.0125 49.0755 28.8781C49.1491 28.3038 49.208 27.7105 49.208 27.1741C49.208 7.40994 32.8574 -0.50712 16.4873 -0.50712H0.136719V11.359C0.136719 35.0912 16.4873 46.9573 28.7551 46.9573C31.2283 46.9573 33.3923 46.5491 35.3895 45.9748C32.033 41.2093 26.3408 34.1656 18.4059 27.9525C16.2959 26.2865 15.972 23.301 17.6748 21.2601C19.3825 19.2381 22.469 18.9058 24.5742 20.5576C31.5718 26.0635 38.6478 32.5898 42.2889 35.9835C42.9219 36.705 43.6482 37.4074 44.2223 38.1336C50.474 46.0127 54.1347 54.6892 54.1151 65.943C54.1151 65.962 54.1151 65.981 54.1151 66L59.0222 65.943H63.9293C63.9293 65.924 63.9293 65.9051 63.9293 65.8861C63.8508 58.8234 66.535 47.0332 72.615 35.2763C74.1067 32.4047 75.7948 29.5094 77.6742 26.6757C81.1435 21.9103 84.8043 17.0879 88.1951 13.8081C90.1138 11.9333 93.22 11.9333 95.1338 13.7702C97.0524 15.6213 97.0721 18.6258 95.1534 20.4816C87.0468 28.3797 81.3545 37.0752 77.8656 46.663C79.6665 46.8624 81.5656 46.9573 83.5578 46.9573C98.2792 46.9573 117.908 32.718 117.908 4.23932V-10H98.2792Z\"\n  />\n</svg>\n";

var plantPacked = "<svg\n  xmlns=\"http://www.w3.org/2000/svg\"\n  xmlns:xlink=\"http://www.w3.org/1999/xlink\"\n  width=\"30pt\"\n  height=\"30pt\"\n  viewBox=\"0 0 30 30\"\n  version=\"1.1\"\n>\n  <g id=\"surface1108873\">\n    <path\n      style=\"stroke: none; fill-rule: nonzero; fill-opacity: 1\"\n      d=\"M 13.984375 -0.015625 C 13.433594 -0.00390625 12.992188 0.449219 13 1 L 13 4 C 12.996094 4.359375 13.183594 4.695312 13.496094 4.878906 C 13.808594 5.058594 14.191406 5.058594 14.503906 4.878906 C 14.816406 4.695312 15.003906 4.359375 15 4 L 15 1 C 15.003906 0.730469 14.898438 0.46875 14.707031 0.277344 C 14.515625 0.0859375 14.253906 -0.0195312 13.984375 -0.015625 Z M 20.175781 0.992188 C 19.988281 0.988281 19.796875 1.039062 19.632812 1.136719 C 19.632812 1.136719 17.351562 2.429688 16.59375 5.027344 C 16.363281 5.816406 16.039062 6.578125 15.644531 7.105469 C 14.914062 7.011719 14.285156 7 14 7 C 13 7 8 7 8 11 C 8 11.816406 8.09375 12.851562 8.261719 14 L 9 14 C 9.550781 14 10 14.445312 10 15 C 10 15.554688 9.550781 16 9 16 L 8.605469 16 C 8.917969 17.613281 9.335938 19.34375 9.8125 21 L 11 21 C 11.550781 21 12 21.445312 12 22 C 12 22.554688 11.550781 23 11 23 L 10.4375 23 C 11.5625 26.359375 12.890625 29 14 29 C 15.441406 29 17.253906 24.546875 18.503906 19.847656 C 18.210938 19.675781 18 19.367188 18 19 C 18 18.460938 18.429688 18.027344 18.964844 18.007812 C 19.292969 16.605469 19.554688 15.234375 19.734375 14 L 17 14 C 16.449219 14 16 13.554688 16 13 C 16 12.445312 16.449219 12 17 12 L 19.960938 12 C 19.984375 11.640625 20 11.304688 20 11 C 20 9.152344 18.933594 8.164062 17.683594 7.628906 C 18.058594 6.96875 18.316406 6.261719 18.511719 5.585938 C 19.011719 3.878906 20.648438 2.863281 20.648438 2.863281 C 21.039062 2.640625 21.234375 2.183594 21.125 1.746094 C 21.015625 1.3125 20.628906 1.003906 20.175781 0.992188 Z M 7.964844 0.996094 C 7.503906 1.003906 7.109375 1.332031 7.011719 1.78125 C 6.914062 2.234375 7.136719 2.695312 7.550781 2.894531 C 7.550781 2.894531 9.136719 3.769531 9.390625 5.179688 C 9.441406 5.539062 9.6875 5.847656 10.03125 5.976562 C 10.375 6.105469 10.761719 6.035156 11.035156 5.792969 C 11.3125 5.554688 11.4375 5.179688 11.359375 4.824219 C 10.890625 2.246094 8.449219 1.105469 8.449219 1.105469 C 8.296875 1.027344 8.132812 0.988281 7.964844 0.996094 Z M 7.964844 0.996094 \"\n    />\n  </g>\n</svg>\n";

var plus = "<svg\n  width=\"15\"\n  height=\"16\"\n  viewBox=\"0 0 15 16\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    fill-rule=\"evenodd\"\n    clip-rule=\"evenodd\"\n    d=\"M7.59043 0.87708C6.93818 0.886616 6.417 1.42155 6.42592 2.07231V6.78873H1.69865C1.27244 6.78272 0.876009 7.00613 0.661147 7.37342C0.446284 7.74071 0.446284 8.19497 0.661147 8.56226C0.876009 8.92955 1.27244 9.15296 1.69865 9.14695H6.42592V13.8634C6.4199 14.2886 6.64382 14.6841 7.01196 14.8985C7.38009 15.1129 7.8354 15.1129 8.20353 14.8985C8.57166 14.6841 8.79559 14.2886 8.78956 13.8634V9.14695H13.5168C13.943 9.15296 14.3395 8.92955 14.5543 8.56226C14.7692 8.19497 14.7692 7.74071 14.5543 7.37342C14.3395 7.00613 13.943 6.78272 13.5168 6.78873H8.78956V2.07231C8.79393 1.7538 8.66896 1.44707 8.44309 1.22194C8.21722 0.996806 7.90967 0.872416 7.59043 0.87708Z\"\n  />\n</svg>\n";

var pound = "<svg\n  width=\"11\"\n  height=\"13\"\n  viewBox=\"-2 0 16 13\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    d=\"M10.6999 13H0.701172V10.8914C2.2849 10.6717 3.36968 9.5212 3.39601 8.0457C3.39601 7.8351 3.35125 7.6323 3.29859 7.449H0.738033V5.5783H2.72987C2.41918 4.8321 2.20459 4.1821 2.20459 3.4437C2.20459 1.2649 4.02792 0 7.16773 0C8.35915 0 9.26752 0.0884 10.1035 0.2899V2.4245C9.24909 2.223 8.4671 2.1346 7.55083 2.1346C5.91444 2.1346 4.90075 2.8197 4.90075 3.926C4.90075 4.5227 5.06136 5.0505 5.27463 5.577H8.98448V7.4477H5.83545C5.88943 7.6232 5.92497 7.8169 5.92497 8.0184C5.97895 9.0818 5.40891 10.0828 4.4571 10.5742V10.7406H10.7012V13H10.6999Z\"\n  />\n</svg>\n";

var recycle = "<svg\n  width=\"16\"\n  height=\"15\"\n  viewBox=\"0 0 16 15\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    fill-rule=\"evenodd\"\n    clip-rule=\"evenodd\"\n    d=\"M6.61964 0.521647L10.7344 0.5C11.1135 0.516333 11.3715 0.711595 11.5255 0.968262L12.3045 2.26937L13.3818 1.69629L11.9059 5.16211L7.91313 4.61068L9.07377 3.99089L7.35032 1.11068C7.04299 0.597344 6.61964 0.521647 6.61964 0.521647ZM3.4783 1.80794C3.94083 1.21498 4.55465 1.04791 5.08822 1.08561C5.62179 1.12331 6.07495 1.36517 6.21625 1.59033L7.03631 2.97575L6.11238 4.41699L2.92756 2.64421L3.42636 1.86263L3.4783 1.80794ZM4.4143 4.76562L0.5 5.32845L1.57127 5.91178L0.778989 7.20605C0.622543 7.46156 0.576156 7.77172 0.753626 8.08789L2.86476 11.4193C2.86476 11.4193 2.71916 11.0365 3.03143 10.526L4.78387 7.66178L5.93968 8.29069L4.4143 4.76562ZM11.5303 6.77539L14.5944 5.10173L15.2961 6.24105L15.3202 6.31055C15.9602 7.65338 14.7281 8.65444 14.1741 8.66553H12.6777L11.5303 6.77539ZM10.5533 8.57552L8.0798 11.5833L10.5533 14.5V13.3333H12.1378C12.4507 13.3333 12.7581 13.2168 12.9591 12.9141L14.9627 9.52344C14.9627 9.52344 14.6845 9.83333 14.0593 9.83333H10.5533V8.57552ZM4.04473 11.2039L4.89256 9.83333H6.84307V13.3219L5.71021 13.3333L5.63533 13.3185C4.08261 13.1698 3.77698 11.6624 4.04473 11.2039Z\"\n  />\n</svg>\n";

var search = "<svg width=\"16\" height=\"16\" viewBox=\"0 0 16 16\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n<path d=\"M6.7998 0.800122C3.4932 0.800122 0.799805 3.49352 0.799805 6.80012C0.799805 10.1067 3.4932 12.8001 6.7998 12.8001C8.23771 12.8001 9.55824 12.2895 10.5932 11.4419L14.1756 15.0243C14.2309 15.0819 14.2971 15.1279 14.3704 15.1596C14.4436 15.1912 14.5225 15.208 14.6023 15.2088C14.6822 15.2096 14.7613 15.1945 14.8352 15.1643C14.9092 15.1341 14.9763 15.0895 15.0327 15.0331C15.0892 14.9766 15.1338 14.9095 15.164 14.8356C15.1942 14.7617 15.2093 14.6825 15.2085 14.6027C15.2077 14.5228 15.1909 14.444 15.1593 14.3707C15.1276 14.2974 15.0816 14.2312 15.024 14.1759L11.4416 10.5935C12.2892 9.55855 12.7998 8.23803 12.7998 6.80012C12.7998 3.49352 10.1064 0.800122 6.7998 0.800122ZM6.7998 2.00012C9.45788 2.00012 11.5998 4.14205 11.5998 6.80012C11.5998 9.4582 9.45788 11.6001 6.7998 11.6001C4.14173 11.6001 1.9998 9.4582 1.9998 6.80012C1.9998 4.14205 4.14173 2.00012 6.7998 2.00012Z\" />\n</svg>\n";

var service = "<svg width=\"20\" height=\"14\" viewBox=\"0 0 20 14\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n<path d=\"M8.53317 0.667059C8.43599 0.665809 8.33951 0.68213 8.24933 0.715073C8.15916 0.748015 8.07708 0.796922 8.00788 0.858952C7.93867 0.920982 7.88372 0.994897 7.84621 1.0764C7.8087 1.15791 7.78939 1.24538 7.78939 1.33373C7.78939 1.42208 7.8087 1.50954 7.84621 1.59105C7.88372 1.67255 7.93867 1.74647 8.00788 1.8085C8.07708 1.87053 8.15916 1.91944 8.24933 1.95238C8.33951 1.98532 8.43599 2.00164 8.53317 2.00039H11.4665C11.5637 2.00164 11.6602 1.98532 11.7503 1.95238C11.8405 1.91944 11.9226 1.87053 11.9918 1.8085C12.061 1.74647 12.1159 1.67255 12.1535 1.59105C12.191 1.50954 12.2103 1.42208 12.2103 1.33373C12.2103 1.24538 12.191 1.15791 12.1535 1.0764C12.1159 0.994897 12.061 0.920982 11.9918 0.858952C11.9226 0.796922 11.8405 0.748015 11.7503 0.715073C11.6602 0.68213 11.5637 0.665809 11.4665 0.667059H8.53317ZM9.99983 3.33373C5.4121 3.33373 1.64554 6.52471 1.23707 10.598C1.19747 10.9914 1.53627 11.3337 1.97041 11.3337H18.0293C18.4634 11.3337 18.8022 10.9914 18.7626 10.598C18.3541 6.52471 14.5876 3.33373 9.99983 3.33373ZM1.19983 12.6671C1.10266 12.6658 1.00618 12.6821 0.916001 12.7151C0.825824 12.748 0.743749 12.7969 0.674544 12.8589C0.60534 12.921 0.550387 12.9949 0.512878 13.0764C0.47537 13.1579 0.456055 13.2454 0.456055 13.3337C0.456055 13.4221 0.47537 13.5095 0.512878 13.591C0.550387 13.6726 0.60534 13.7465 0.674544 13.8085C0.743749 13.8705 0.825824 13.9194 0.916001 13.9524C1.00618 13.9853 1.10266 14.0016 1.19983 14.0004H18.7998C18.897 14.0016 18.9935 13.9853 19.0837 13.9524C19.1738 13.9194 19.2559 13.8705 19.3251 13.8085C19.3943 13.7465 19.4493 13.6726 19.4868 13.591C19.5243 13.5095 19.5436 13.4221 19.5436 13.3337C19.5436 13.2454 19.5243 13.1579 19.4868 13.0764C19.4493 12.9949 19.3943 12.921 19.3251 12.8589C19.2559 12.7969 19.1738 12.748 19.0837 12.7151C18.9935 12.6821 18.897 12.6658 18.7998 12.6671H1.19983Z\" />\n</svg>";

var sesame = "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 30 30\" fill=\"#fc4a5a\">\n  <path\n    d=\"M 22 3 C 13.659 3 9.6128594 9.8703437 5.8808594 15.652344 C 5.3698594 16.443344 5.4824375 17.482438 6.1484375 18.148438 L 11.851562 23.851562 C 12.517563 24.517563 13.556656 24.629141 14.347656 24.119141 C 20.129656 20.387141 27 16.341 27 8 C 27 4 25.294922 4.7070313 25.294922 4.7070312 L 25.296875 4.703125 L 25.292969 4.7050781 C 25.292969 4.7050781 26 3 22 3 z M 21.421875 4.921875 C 21.853875 4.921875 22.203125 5.3185781 22.203125 5.8925781 C 22.203125 6.4665781 21.421875 7.3222656 21.421875 7.3222656 C 21.421875 7.3222656 20.638672 6.5725781 20.638672 5.8925781 C 20.637672 5.2105781 20.988875 4.921875 21.421875 4.921875 z M 15.980469 6.2734375 C 16.412469 6.2734375 16.761719 6.6701406 16.761719 7.2441406 C 16.761719 7.8161406 15.980469 8.6738281 15.980469 8.6738281 C 15.980469 8.6738281 15.197266 7.9251406 15.197266 7.2441406 C 15.197266 6.5631406 15.548469 6.2734375 15.980469 6.2734375 z M 18.695312 6.4453125 C 19.343312 6.4453125 19.869141 7.0433438 19.869141 7.9023438 C 19.869141 8.7613437 18.695312 10.044922 18.695312 10.044922 C 18.695312 10.044922 17.521484 8.9233438 17.521484 7.9023438 C 17.521484 6.8813438 18.047312 6.4453125 18.695312 6.4453125 z M 24.107422 7.7988281 C 24.681422 7.7988281 25.078125 8.1480781 25.078125 8.5800781 C 25.078125 9.0120781 24.789422 9.3613281 24.107422 9.3613281 C 23.427422 9.3613281 22.677734 8.5800781 22.677734 8.5800781 C 22.677734 8.5800781 23.534422 7.7988281 24.107422 7.7988281 z M 13.255859 8.9980469 C 13.687859 8.9980469 14.037109 9.3967031 14.037109 9.9707031 C 14.037109 10.544703 13.255859 11.398438 13.255859 11.398438 C 13.255859 11.398438 12.474609 10.650703 12.474609 9.9707031 C 12.473609 9.2887031 12.823859 8.9980469 13.255859 8.9980469 z M 15.970703 9.1699219 C 16.618703 9.1699219 17.144531 9.7679531 17.144531 10.626953 C 17.144531 11.485953 15.970703 12.769531 15.970703 12.769531 C 15.970703 12.769531 14.796875 11.647953 14.796875 10.626953 C 14.796875 9.6059531 15.322703 9.1699219 15.970703 9.1699219 z M 22.097656 10.130859 C 22.956656 10.130859 23.554688 10.656688 23.554688 11.304688 C 23.554688 11.952687 23.118656 12.478516 22.097656 12.478516 C 21.076656 12.478516 19.955078 11.304688 19.955078 11.304688 C 19.955078 11.304688 21.238656 10.130859 22.097656 10.130859 z M 10.519531 11.734375 C 10.951531 11.734375 11.302734 12.131078 11.302734 12.705078 C 11.302734 13.279078 10.519531 14.134766 10.519531 14.134766 C 10.519531 14.134766 9.7382812 13.387031 9.7382812 12.707031 C 9.7372813 12.025031 10.087531 11.734375 10.519531 11.734375 z M 13.246094 11.894531 C 13.894094 11.894531 14.419922 12.492562 14.419922 13.351562 C 14.419922 14.210562 13.246094 15.494141 13.246094 15.494141 C 13.246094 15.494141 12.072266 14.372563 12.072266 13.351562 C 12.072266 12.330562 12.598094 11.894531 13.246094 11.894531 z M 19.373047 12.855469 C 20.232047 12.855469 20.830078 13.381297 20.830078 14.029297 C 20.830078 14.677297 20.394047 15.203125 19.373047 15.203125 C 18.352047 15.203125 17.230469 14.029297 17.230469 14.029297 C 17.230469 14.029297 18.514047 12.855469 19.373047 12.855469 z M 22.755859 13.238281 C 23.329859 13.238281 23.726562 13.589484 23.726562 14.021484 C 23.726562 14.453484 23.436859 14.802734 22.755859 14.802734 C 22.073859 14.802734 21.326172 14.021484 21.326172 14.021484 C 21.326172 14.021484 22.183859 13.238281 22.755859 13.238281 z M 10.521484 14.619141 C 11.169484 14.619141 11.695312 15.217172 11.695312 16.076172 C 11.695312 16.935172 10.521484 18.220703 10.521484 18.220703 C 10.521484 18.220703 9.3496562 17.097172 9.3476562 16.076172 C 9.3476562 15.054172 9.8734844 14.619141 10.521484 14.619141 z M 16.648438 15.580078 C 17.507437 15.580078 18.105469 16.105906 18.105469 16.753906 C 18.105469 17.401906 17.669438 17.927734 16.648438 17.927734 C 15.627437 17.927734 14.505859 16.753906 14.505859 16.753906 C 14.505859 16.753906 15.789438 15.580078 16.648438 15.580078 z M 20.029297 15.962891 C 20.603297 15.962891 21.001953 16.312141 21.001953 16.744141 C 21.001953 17.176141 20.711297 17.527344 20.029297 17.527344 C 19.349297 17.527344 18.601563 16.744141 18.601562 16.744141 C 18.601562 16.744141 19.456297 15.962891 20.029297 15.962891 z M 13.923828 18.304688 C 14.782828 18.304688 15.380859 18.830516 15.380859 19.478516 C 15.380859 20.126516 14.944828 20.652344 13.923828 20.652344 C 12.902828 20.651344 11.78125 19.478516 11.78125 19.478516 C 11.78125 19.478516 13.064828 18.304688 13.923828 18.304688 z M 17.294922 18.699219 C 17.868922 18.699219 18.265625 19.048469 18.265625 19.480469 C 18.265625 19.912469 17.974969 20.263672 17.292969 20.263672 C 16.612969 20.263672 15.865234 19.480469 15.865234 19.480469 C 15.865234 19.480469 16.721922 18.699219 17.294922 18.699219 z M 3.4199219 18.998047 C 2.5669219 19.851047 3.1851094 21.22525 3.7871094 22.15625 C 4.1821094 22.76725 4.1126562 23.567031 3.5976562 24.082031 L 3.0195312 24.660156 L 5.3496094 26.990234 L 5.9375 26.400391 C 6.4525 25.885391 7.2532344 25.815938 7.8652344 26.210938 C 8.7912344 26.809937 10.155 27.421172 11 26.576172 L 3.4199219 18.998047 z\"\n  />\n</svg>\n";

var sharing = "<svg\n  width=\"15\"\n  height=\"15\"\n  viewBox=\"0 0 15 15\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    fill-rule=\"evenodd\"\n    clip-rule=\"evenodd\"\n    d=\"M2.5 0C1.11929 0 0 1.11929 0 2.5V12.5C0 13.8807 1.11929 15 2.5 15H12.5C13.8807 15 15 13.8807 15 12.5V2.5C15 1.11929 13.8807 0 12.5 0H2.5ZM1 2.5C1 1.67157 1.67157 1 2.5 1H12.5C13.3284 1 14 1.67157 14 2.5V12.5C14 13.3284 13.3284 14 12.5 14H2.5C1.67157 14 1 13.3284 1 12.5V2.5ZM3 2C2.4475 2 2 2.4475 2 3V6C2 6.5525 2.4475 7 3 7H6C6.5525 7 7 6.5525 7 6V3C7 2.4475 6.5525 2 6 2H3ZM9 2C8.4475 2 8 2.4475 8 3V6C8 6.5525 8.4475 7 9 7H12C12.5525 7 13 6.5525 13 6V3C13 2.4475 12.5525 2 12 2H9ZM2 9C2 8.4475 2.4475 8 3 8H6C6.5525 8 7 8.4475 7 9V12C7 12.5525 6.5525 13 6 13H3C2.4475 13 2 12.5525 2 12V9ZM9 8C8.4475 8 8 8.4475 8 9V12C8 12.5525 8.4475 13 9 13H12C12.5525 13 13 12.5525 13 12V9C13 8.4475 12.5525 8 12 8H9Z\"\n  />\n</svg>\n";

var sourced = "<svg\n  width=\"13\"\n  height=\"11\"\n  viewBox=\"0 0 13 11\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    fill-rule=\"evenodd\"\n    clip-rule=\"evenodd\"\n    d=\"M5.61009 5.69655e-05C4.97195 -0.00332615 4.26862 0.143839 3.54545 0.479191C3.87752 3.21613 5.51988 3.78619 6.68752 3.78619C6.44615 3.37006 6.12118 2.99834 5.84249 2.73869C5.66158 2.57038 5.66914 2.30269 5.85572 2.14072C6.0234 1.99863 6.26902 1.99398 6.44946 2.10774C7.49193 3.04613 8.26895 4.32156 8.26895 5.90063H8.7413L9.21365 5.90909C9.21932 5.6562 9.39267 5.11744 9.70253 4.55923C10.044 4.0488 10.6231 3.37641 10.9774 3.06093C11.0331 3.01991 11.0903 2.98016 11.1474 2.94041C11.3284 2.85794 11.5532 2.88078 11.6992 3.01991C11.8725 3.18822 11.8631 3.45083 11.6751 3.60773C11.3855 3.84877 10.9703 4.23529 10.7101 4.63196C12.0605 4.63196 13 4.02892 13 1.68316C11.2192 0.694018 9.4536 1.52796 9.02707 3.02625C8.95905 3.26265 8.94252 3.58954 9.02565 3.90671C8.98503 3.96972 8.93874 4.03231 8.9019 4.09363C8.73563 3.62591 8.5089 3.1916 8.23777 2.78859C8.36484 2.44309 8.37428 2.06968 8.31524 1.80368C8.08237 0.743497 7.01061 0.00809186 5.61009 5.69655e-05ZM2.72727 4.72727C2.55318 4.72727 2.38091 4.76371 2.21955 4.83511L0 5.71212V9.65152L1.91773 8.74939C2.145 8.64155 2.40045 8.63761 2.62955 8.73955L6.74909 10.5635C6.74909 10.5635 6.75091 10.5635 6.75227 10.5635C6.84318 10.6112 6.94409 10.6364 7.04545 10.6364C7.16273 10.6364 7.27818 10.6019 7.38091 10.5384C7.38091 10.5384 7.38273 10.5403 7.38455 10.5384L11.4682 8.08167V8.08019C11.6832 7.9492 11.8182 7.70299 11.8182 7.43561C11.8182 7.02788 11.5127 6.69697 11.1364 6.69697C11.0368 6.69697 10.9373 6.72208 10.8468 6.76837V6.7664L7.72727 8.17424H7.04545C7.42182 8.17424 7.72727 7.84333 7.72727 7.43561C7.72727 7.11652 7.53727 6.83337 7.25682 6.73341L3.30591 4.86564H3.29909C3.11955 4.77553 2.92455 4.72727 2.72727 4.72727Z\"\n  />\n</svg>\n";

var soybeans = "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 30 30\" fill=\"#fc4a5a\">\n  <path\n    d=\"M24.217,3C22.072,3,17.515,5.822,17,10.5c-3.798,0.415-6.405,3.412-7,6.5c-1.163,0.241-7,2.371-7,8c0,1.121,1.927,2,5.353,2 c4.435,0,6.774-3.097,6.774-3.097c6.506-0.439,7.968-5.093,8.1-7.188c0,0,3.843-2.362,3.843-6.967C27.07,5.96,25.922,3,24.217,3z M9.581,24.005c-0.975,0.696-2.178,0.682-2.686-0.031c-0.508-0.713-0.129-1.854,0.846-2.55c0.975-0.695,2.178-0.681,2.686,0.031 C10.935,22.168,10.556,23.309,9.581,24.005z M18.874,18.874c-1.294,1.294-3.182,1.503-4.217,0.469 c-1.035-1.035-0.825-2.923,0.469-4.217c1.294-1.294,3.182-1.503,4.217-0.469S20.168,17.58,18.874,18.874z M24.072,9.778 c-0.51,1.33-1.718,2.108-2.697,1.738c-0.979-0.37-1.36-1.748-0.85-3.078c0.51-1.33,1.718-2.108,2.697-1.738 S24.582,8.448,24.072,9.778z\"\n  />\n</svg>\n";

var sprout = "<svg\n  width=\"48\"\n  height=\"65\"\n  viewBox=\"-5 0 70 50\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    d=\"M42.196 -6C23.5629 -6 4.92977 2.94118 4.92977 25.2941C4.92977 30.4129 5.71236 34.3918 7.13314 37.5167C6.18751 39.6492 4.58506 43.8068 4.58506 43.8068C4.58506 43.8068 0.290118 54.9296 0.271484 65.5294C0.271484 65.5473 0.271484 65.5652 0.271484 65.5831L4.92977 65.5294H9.58805C9.58805 65.5115 9.58805 65.4936 9.58805 65.4758C9.51352 58.8235 12.0616 47.7186 17.8332 36.6449C19.2493 33.9402 20.8518 31.2132 22.6359 28.5442C25.9293 24.0558 29.4044 19.5136 32.6233 16.4245C34.4447 14.6586 37.3934 14.6586 39.2101 16.3887C41.0315 18.1322 41.0501 20.9621 39.2287 22.7101C31.5332 30.1492 26.1296 38.3393 22.8176 47.3699C24.5272 47.5576 26.3299 47.6471 28.2212 47.6471C42.196 47.6471 60.8292 34.2353 60.8292 7.41177V-6H42.196Z\"\n  />\n</svg>\n";

var star = "<svg\n  width=\"14\"\n  height=\"13\"\n  viewBox=\"0 0 14 13\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    d=\"M7.39919 0.2791L8.89844 4.59954L13.5829 4.65232C13.986 4.65689 14.1529 5.15685 13.8295 5.39084L10.0712 8.11344L11.4672 12.4664C11.5872 12.841 11.1507 13.1496 10.8217 12.9227L7.00026 10.2848L3.17835 12.9222C2.84982 13.149 2.41282 12.8399 2.53276 12.4659L3.92876 8.11294L0.170465 5.39033C-0.152851 5.15634 0.0140218 4.65638 0.417124 4.65182L5.10156 4.59903L6.60081 0.278592C6.73013 -0.0929487 7.27039 -0.0929487 7.39919 0.2791Z\"\n  />\n</svg>\n";

var sulphur = "<svg width=\"8\" height=\"8\" viewBox=\"0 0 8 8\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n  <mask id=\"mask0_5732_5560\" style=\"mask-type:alpha\" maskUnits=\"userSpaceOnUse\" x=\"0\" y=\"0\" width=\"8\" height=\"8\">\n    <path fill-rule=\"evenodd\" clip-rule=\"evenodd\" d=\"M0.293945 0.292969H7.70571V7.70473H0.293945V0.292969Z\" fill=\"white\" />\n  </mask>\n  <g mask=\"url(#mask0_5732_5560)\">\n    <path fill-rule=\"evenodd\" clip-rule=\"evenodd\" d=\"M2.3561 0.46011C2.41809 0.354999 2.53129 0.291663 2.65223 0.292989H5.34742C5.46837 0.291663 5.58157 0.354999 5.64355 0.46011C5.7042 0.565559 5.7042 0.694253 5.64355 0.799701C5.58157 0.904813 5.46837 0.968149 5.34742 0.966801V3.66197L7.5531 6.60441C7.65181 6.7257 7.70571 6.87561 7.70571 7.03092C7.70571 7.40319 7.40419 7.70471 7.03191 7.70471H0.967742C0.595469 7.70471 0.293945 7.40319 0.293945 7.03092C0.293945 6.87561 0.347849 6.7257 0.44656 6.60441L2.65223 3.66197V0.966801C2.53129 0.968149 2.41809 0.904813 2.3561 0.799701C2.29546 0.694253 2.29546 0.565559 2.3561 0.46011ZM4.67349 2.98757H3.3259V0.966199H4.67349V2.98757ZM3.6628 3.66234C3.84843 3.66234 3.9997 3.81361 3.9997 3.99924C3.9997 4.18487 3.84843 4.33613 3.6628 4.33613C3.47717 4.33613 3.3259 4.18487 3.3259 3.99924C3.3259 3.81361 3.47717 3.66234 3.6628 3.66234ZM5.34725 6.18899C5.34725 5.91004 5.12085 5.68365 4.8419 5.68365C4.56295 5.68365 4.33655 5.91004 4.33655 6.18899C4.33655 6.46794 4.56295 6.69433 4.8419 6.69433C5.12085 6.69433 5.34725 6.46794 5.34725 6.18899Z\" />\n  </g>\n</svg>\n";

var timer = "<svg\n  width=\"20\"\n  height=\"20\"\n  viewBox=\"0 0 20 20\"\n  fill=\"none\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <path\n    fill-rule=\"evenodd\"\n    clip-rule=\"evenodd\"\n    d=\"M9.16667 0V4.16667C9.16333 4.46583 9.32 4.74583 9.58 4.89917C9.84083 5.04917 10.1592 5.04917 10.42 4.89917C10.68 4.74583 10.8367 4.46583 10.8333 4.16667V1.83583C15.0325 2.26917 18.3333 5.68333 18.3333 10C18.3333 14.6125 14.6125 18.3333 10 18.3333C5.3875 18.3333 1.66667 14.6125 1.66667 10C1.66667 7.95583 2.4025 6.09333 3.62 4.645C3.91917 4.29333 3.87333 3.76667 3.5225 3.47C3.1675 3.17417 2.64333 3.21917 2.34333 3.57083C0.8825 5.30583 0 7.555 0 10C0 15.5142 4.48583 20 10 20C15.5142 20 20 15.5142 20 10C20 4.48583 15.5142 0 10 0H9.16667ZM5.32917 5.00667C5.085 5.0425 4.9025 5.32917 5.065 5.5825C6.28583 7.49667 8.36917 10.7258 8.82167 11.1783C9.4725 11.8292 10.5275 11.8292 11.1783 11.1783C11.8292 10.5275 11.8292 9.4725 11.1783 8.82167C10.7258 8.36583 7.49667 6.28583 5.5825 5.065C5.49833 5.01 5.41 4.99333 5.32917 5.00667Z\"\n  />\n</svg>\n";

var icons = {
    bag: bag,
    basket: basket,
    burger: burger,
    calendar: calendar,
    celery: celery,
    cereals: cereals,
    chevronDown: chevronDown,
    chevronLeft: chevronLeft,
    chevronRight: chevronRight,
    chevronUp: chevronUp,
    close: close,
    crustaceans: crustaceans,
    eggs: eggs,
    euro: euro,
    eye: eye,
    filter: filter,
    fish: fish,
    halal: halal,
    heart: heart,
    highProtein: highProtein,
    individual: individual,
    info: info,
    knifeFork: knifeFork,
    leaf: leaf,
    locationPin: locationPin,
    lowCarb: lowCarb,
    lupin: lupin,
    milk: milk,
    minus: minus,
    molluscs: molluscs,
    mustard: mustard,
    noGluten: noGluten,
    noMilk: noMilk,
    none: none,
    noNuts: noNuts,
    noSugar: noSugar,
    note: note,
    nuts: nuts,
    order: order,
    peanuts: peanuts,
    person: person,
    plant: plant,
    plantPacked: plantPacked,
    plus: plus,
    pound: pound,
    recycle: recycle,
    search: search,
    service: service,
    sesame: sesame,
    sharing: sharing,
    sourced: sourced,
    soybeans: soybeans,
    sprout: sprout,
    star: star,
    sulphur: sulphur,
    timer: timer,
};
var SVGContainer = styled.span(templateObject_1$2 || (templateObject_1$2 = __makeTemplateObject$2(["\n  display: flex;\n  ", "\n"], ["\n  display: flex;\n  ", "\n"])), function (p) {
    return p.variation === 'circle' &&
        "\n    border-radius: 100%;\n    background-color: ".concat(p.fill, ";\n    padding: 6px;\n  ");
});
var Icon = function (_a) {
    var icon = _a.icon, _b = _a.fill, fill = _b === void 0 ? 'currentColor' : _b, _c = _a.size, size = _c === void 0 ? 20 : _c, _d = _a.variation, variation = _d === void 0 ? 'standard' : _d, className = _a.className;
    var theme = useContext(ThemeContext);
    return (React__default.createElement(SVGContainer, { className: className, variation: variation, fill: fill },
        React__default.createElement(SVG, { src: icons[icon], fill: variation === 'circle' ? theme.colors.canvas : fill, width: size, height: size })));
};
Icon.displayName = 'Icon';
var templateObject_1$2;

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __makeTemplateObject$1(cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __makeTemplateObject(cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
}

createGlobalStyle(templateObject_1$1 || (templateObject_1$1 = __makeTemplateObject(["\n  * {\n    font-family: ", ";\n    box-sizing: border-box;\n  }\n\n  body {\n    margin: 0;\n  }\n"], ["\n  * {\n    font-family: ", ";\n    box-sizing: border-box;\n  }\n\n  body {\n    margin: 0;\n  }\n"])), function (p) { return p.theme.fontFaces.default; });
var templateObject_1$1;

var breakpoints = {
    s: 600,
    m: 1024,
    l: 1440,
    xl: 1920,
};
var mobileMediaQuery = "(max-width: ".concat(breakpoints.s - 1, "px)");

var Wrapper = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject$1(["\n  display: inline-flex;\n  border: ", ";\n  border-radius: 28px;\n  padding: ", ";\n  width: auto;\n"], ["\n  display: inline-flex;\n  border: ", ";\n  border-radius: 28px;\n  padding: ", ";\n  width: auto;\n"])), function (p) {
    return lib.match(p.border)
        .with(false, function () { return 0; })
        .with(true, function () { return "1px solid ".concat(p.theme.colors.background_dark); })
        .exhaustive();
}, function (p) {
    return lib.match(p.size)
        .with('small', function () { return (p.border ? "6px 8px" : 0); })
        .with('large', function () { return (p.border ? "8px 12px" : 0); })
        .exhaustive();
});
var StyledNumberPicker = styled.input(templateObject_2 || (templateObject_2 = __makeTemplateObject$1(["\n  border: 0;\n  padding: 0;\n  margin: 0 6px;\n  width: ", ";\n  text-align: center;\n  -webkit-appearance: none;\n  font-family: ", ";\n  font-weight: ", ";\n  font-size: ", ";\n  color: ", ";\n  line-height: 1.22;\n\n  &::-webkit-inner-spin-button {\n    -webkit-appearance: none;\n  }\n\n  &[type='number'] {\n    -moz-appearance: textfield;\n  }\n\n  @media ", " {\n    font-size: ", ";\n  }\n"], ["\n  border: 0;\n  padding: 0;\n  margin: 0 6px;\n  width: ", ";\n  text-align: center;\n  -webkit-appearance: none;\n  font-family: ", ";\n  font-weight: ", ";\n  font-size: ", ";\n  color: ", ";\n  line-height: 1.22;\n\n  &::-webkit-inner-spin-button {\n    -webkit-appearance: none;\n  }\n\n  &[type='number'] {\n    -moz-appearance: textfield;\n  }\n\n  @media ", " {\n    font-size: ", ";\n  }\n"])), function (p) {
    return lib.match(p.inputSize)
        .with('small', function () { return '23px'; })
        .with('large', function () { return '38px'; })
        .exhaustive();
}, function (p) { return p.theme.fontFaces.default; }, function (p) {
    return lib.match(p.inputSize)
        .with('small', function () { return p.theme.fontWeights.medium; })
        .with('large', function () { return p.theme.fontWeights.semi; })
        .exhaustive();
}, function (p) {
    return lib.match(p.inputSize)
        .with('small', function () { return p.theme.fontSizes.s; })
        .with('large', function () { return p.theme.fontSizes.xl; })
        .exhaustive();
}, function (p) { return p.theme.colors.text_heading; }, mobileMediaQuery, function (p) {
    return lib.match(p.inputSize)
        .with('small', function () { return p.theme.fontSizes.base; })
        .with('large', function () { return p.theme.fontSizes.l; })
        .exhaustive();
});
var IconButton = styled.button(templateObject_3 || (templateObject_3 = __makeTemplateObject$1(["\n  border: 0;\n  background: none;\n  cursor: pointer;\n  padding: 0;\n"], ["\n  border: 0;\n  background: none;\n  cursor: pointer;\n  padding: 0;\n"])));
var templateObject_1, templateObject_2, templateObject_3;

var NumberPicker = function (_a) {
    var value = _a.value, onChange = _a.onChange, _b = _a.min, min = _b === void 0 ? 0 : _b, max = _a.max, _c = _a.size, size = _c === void 0 ? 'large' : _c, _d = _a.border, border = _d === void 0 ? true : _d;
    var theme = useContext(ThemeContext);
    var _e = useState(false), editing = _e[0], setEditing = _e[1];
    var getValidValue = function (newValue) {
        if (newValue < min)
            return min;
        if (typeof max === 'number' && newValue > max)
            return max;
        // if new value contains decimal, return the previous valid value
        if (!Number.isInteger(newValue))
            return value;
        return newValue;
    };
    var iconSize = lib.match(size)
        .with('small', function () { return 8; })
        .with('large', function () { return 15; })
        .exhaustive();
    return (React__default.createElement(Wrapper, { size: size, border: border },
        React__default.createElement(IconButton, { onClick: function () { return onChange(value - 1); }, disabled: value <= min },
            React__default.createElement(Icon, { icon: "minus", variation: "circle", size: iconSize, fill: value <= min
                    ? theme.colors.text_most_muted
                    : theme.colors.text_semi_muted })),
        React__default.createElement(StyledNumberPicker, { type: "number", id: "quantity", step: "1", min: min, max: max, inputMode: "numeric", pattern: "[0-9]*", value: editing ? undefined : value, onFocus: function () { return setEditing(true); }, onBlur: function (event) {
                setEditing(false);
                var newValue = Number(event.target.value);
                onChange(getValidValue(newValue));
            }, inputSize: size }),
        React__default.createElement(IconButton, { onClick: function () { return onChange(value + 1); }, disabled: typeof max === 'number' ? value >= max : false },
            React__default.createElement(Icon, { icon: "plus", variation: "circle", size: iconSize, fill: typeof max === 'number' && value >= max
                    ? theme.colors.text_most_muted
                    : theme.colors.text_semi_muted }))));
};
NumberPicker.displayName = 'NumberPicker';

export { NumberPicker as default };
//# sourceMappingURL=index.esm.js.map
